// G.U.L.P. VISOR MK.II, browser build ("HELIOGRAPH" line). Light projected onto the curved visor
// of a contractor-issue helmet: every instrument is crisp phosphor symbology with a 1-px dark key
// (so it holds on the brightest gold haze), seated on a patch of smoked glass that the post pass
// (render/post.ts, visor block) frosts and darkens ADAPTIVELY to what's behind it, then bends,
// splits and blooms as one piece of glass.
//
// ONE DESIGN SYSTEM (see client/lab/hud_LOOP.md for the full token sheet):
//  - shape: circles for gauges, 45-degree chamfers for every plate/tab, nothing else
//  - stroke: hairline (1 device px) for scales/frames, 2u for symbology, bands for gauges;
//    every stroke over the world carries the key
//  - type: Space Grotesk (OFL, public/assets/hud), light weights; numerals in fixed tabular cells
//    with +10% tracking for labels; every text run sits on glass or on a drawn rail
//  - colour: one phosphor + shield cyan, caution amber, alarm red (solid plates only), and prime
//    magenta reserved for the prime/detonate gameplay read
//  - every instrument carries a contractor part number (LIFE / ORDNANCE / WORK ORDER ...)
//
// Two canvases: the light (premultiplied RGBA, mipmapped for the shader bloom) and a half-res
// GLASS mask (alpha = glass density). The glass texture rides on texture.userData.glass, which
// PostPipeline.setHud picks up. Redraws are per-layer dirty rects: a layer's signature changes,
// its old+new rects are cleared and every layer touching them is redrawn under a clip.
//
// Conventions the game client feeds:
//  - HudTeammate.angle / HudMarker.angle: screen-space direction, atan2(dy, dx), y down.
//  - hurtDirs[].angle: relative to view forward, radians, 0 = in front, +PI/2 = from the right.
//  - HudMarker.t: spawn/bomb = seconds until it lands; downed = revive progress 0..1;
//    gate = charge 0..1; primed = unused.
//  - bleedOut: SECONDS left in the 30-s claim window (what game.ts feeds: phaseT / TICK_RATE).
//  - reviveFrac, skyT, chainFrac, abilityFrac, reloadFrac: 0..1 (reloadFrac -1 = not reloading).
//  - net.loss: 0..1 fraction. net.tris: raw triangle count.

import * as THREE from 'three';
import type { HudApi, HudSight, HudState, HudTeammate, HudMarker, HudPickup } from '../render/contracts';
import { PAL, SLOT_CSS } from '../render/palette';

/** Button glyphs per action. kbm = key cap / mouse, pad = PS5 glyph. The lead may rebind here. */
/** the hand has no button: it's a readout */
const HAND_BIND = { kbm: '', pad: '' };
export const BIND = {
  reload: { kbm: 'R', pad: '□' },
  revive: { kbm: 'E', pad: '□' },
  ability: { kbm: 'Q', pad: '○○' },
  grenade: { kbm: 'G', pad: 'R1' },
  swap: { kbm: 'X', pad: '△' },
  special: { kbm: 'T', pad: '←' },
  melee: { kbm: 'F', pad: 'R3' },
  ping: { kbm: 'Z', pad: '↑' },
  powerup: { kbm: 'B', pad: '↓' },
  drink: { kbm: 'H', pad: '→' },
} as const;

// ------------------------------------------------------------------ tokens
const rgba = (hex: number, a = 1) => `rgba(${(hex >> 16) & 255},${(hex >> 8) & 255},${hex & 255},${a})`;
const PHOSPHOR = 0xe4fff4; // the visor's light: cool white, the complement of the gold haze
const T = {
  // projected light (James, 2026-10-21): translucent, no dark keys, no backing cards
  phos: rgba(PHOSPHOR, 0.88),
  phos80: rgba(PHOSPHOR, 0.7),
  phos60: rgba(PHOSPHOR, 0.52),
  phos42: rgba(PHOSPHOR, 0.36),
  phos26: rgba(PHOSPHOR, 0.22),
  phos14: rgba(PHOSPHOR, 0.12),
  key: 'rgba(0,0,0,0)',
  keySoft: 'rgba(0,0,0,0)',
  keyHard: 'rgba(0,0,0,0)',
  tabFill: rgba(PHOSPHOR, 0.05),
  cardSmoke: rgba(PHOSPHOR, 0.04),
  shield: rgba(PAL.shieldBlue, 0.9),
  shield60: rgba(PAL.shieldBlue, 0.52),
  shield20: rgba(PAL.shieldBlue, 0.18),
  amber: rgba(PAL.primeAmber, 0.9),
  amber60: rgba(PAL.primeAmber, 0.52),
  amber25: rgba(PAL.primeAmber, 0.22),
  red: rgba(PAL.hurtRed, 0.92),
  red60: rgba(PAL.hurtRed, 0.52),
  red25: rgba(PAL.hurtRed, 0.22),
  plateRed: 'rgba(230,52,40,0.42)',
  plateRedEdge: 'rgba(255,110,90,0.85)',
  plateText: 'rgba(255,240,232,0.95)',
  prime: rgba(PAL.primeMagenta, 0.92),
  prime60: rgba(PAL.primeMagenta, 0.52),
  prime25: rgba(PAL.primeMagenta, 0.22),
  primePlate: 'rgba(230,40,150,0.4)',
  hyper: rgba(PAL.hyperMagenta, 0.92),
  glass: '#fff',
};
const SLOT = SLOT_CSS;
const TAU = Math.PI * 2;
// gauge sweep: 270 degrees, open at the bottom where the instrument's data plate sits
const SW = Math.PI * 1.5;
const A0 = Math.PI * 0.75;
const ENAME = ['SWARMER', 'SHOOTER', 'BRUTE', 'SNIPER', 'BOMBER', 'METAL BRUTE'];
const ECODE = ['SW-1', 'SH-2', 'BR-3', 'SN-4', 'BM-5', 'MB-9'];
const CLS3 = ['GSL', 'VLK', 'PHM', 'JGN'];
const VTL_SN = ['LIFE · VTL-02/GSL', 'LIFE · VTL-02/VLK', 'LIFE · VTL-02/PHM', 'LIFE · VTL-02/JGN'];
const ORD_SN = ['ORDNANCE · ISSUED GSL-01', 'ORDNANCE · ISSUED VLK-07', 'ORDNANCE · ISSUED PHM-03', 'ORDNANCE · ISSUED JGN-11'];
const DIG = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const NET_HIST = 96;
const MAJ = [0, 0.45, 0.72, 0.8, 1];
// the game-wide UI/HUD face (James, 2026-10-21): Space Grotesk; the western face is for the title only
const FAMILY = 'GulpGrotesk';
/** regular / emphasis weights: kept light, it's projected light */
const W0 = 400, W1 = 520;
const GS = 0.5; // glass mask resolution
/** stroke weight: hairline projection (James, 2026-10-21) */
const LINE = 0.72;

// layers, in paint order
const L_VISOR = 0, L_FLOAT = 1, L_CENTER = 2, L_TOP = 3, L_POSSE = 4, L_LOG = 5, L_VITALS = 6, L_ORD = 7, L_NET = 8, L_ALARM = 9, L_CALL = 10, L_PICK = 11;
const NL = 12;
const MAXR = 40; // rects per layer
const MAXD = 96; // dirty rects per redraw

// shutter / appear keys
const K_SHIELD = 0, K_OVER = 1, K_FIRE = 2, K_DENIED = 3, K_COACH = 4, K_TARGET = 5, K_PRIMED = 6, K_CHAIN = 7,
  K_DOWN = 8, K_SKY = 9, K_WIPE = 10, K_CLEAR = 11, K_RELOAD = 12, K_EMPTY = 13, K_HYPER = 14, K_LOWHP = 15, K_ABILITY = 16;
const NK = 17;

let fontReady = false;
let fontPromise: Promise<void> | null = null;
function loadFont(): Promise<void> {
  if (fontPromise) return fontPromise;
  const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
  const ff = new FontFace(FAMILY, `url(${base}assets/hud/SpaceGrotesk-Variable.woff2)`, { weight: '300 700' });
  fontPromise = ff.load().then((f) => { document.fonts.add(f); fontReady = true; }).catch(() => { fontReady = true; });
  return fontPromise;
}

export class Hud implements HudApi {
  readonly texture: THREE.CanvasTexture;
  readonly glassTexture: THREE.CanvasTexture;
  private canvas: HTMLCanvasElement;
  private gcanvas: HTMLCanvasElement;
  private c: CanvasRenderingContext2D;
  private g: CanvasRenderingContext2D;
  private w = 1; private h = 1; private dpr = 1; private u = 1; private kp = 1.2; private hl = 1;
  private s!: HudState;
  private fontOk = false;

  // per-layer signature + rects
  private sig: (number | string)[][] = [];
  private si = 0; private cur = 0;
  private dirty = new Uint8Array(NL);
  private rects: Float32Array[] = [];
  private nr = new Int32Array(NL);
  private prevRects = new Float32Array(MAXR * 4);
  private nPrev = 0;
  private dr = new Float32Array(MAXD * 4);
  private nd = 0;
  private forceFull = true;

  // text caches
  private fontCache = new Map<number, string>();
  private lsCache = new Map<number, string>();
  private curFont = ''; private curLS = '';
  private adv = [new Float32Array(128), new Float32Array(128)];

  // motion state
  private onT = new Float64Array(NK).fill(-1);
  private ap = new Float32Array(NK);
  private lastT = 0;
  private ghHull = 1; private ghShield = 1; private lastHp = 1; private lastSh = 1; private holdHull = 0; private holdSh = 0;
  private lastMag = -1; private magAt = -9; private lastHullN = -1; private hullAt = -9;
  private hPing = new Float32Array(NET_HIST);
  private hFrame = new Float32Array(NET_HIST);
  private hIdx = 0; private hLast = -1;
  private ep = new Float32Array(2);
  /** gauge-band uniforms read by the post pass (see post.ts hudRings) */
  readonly rings = new Float32Array(24);
  private spr: { cv: HTMLCanvasElement; ctx: CanvasRenderingContext2D; k1: number; k2: string; lay: number }[] = [];
  private gNull!: CanvasRenderingContext2D;
  private layVer = 0;
  spriteBuilds = 0;
  // calm-at-rest bookkeeping: when did each group last change
  private woKey = -1; private woAt = -99; private livesN = -1; private livesAt = -99;
  private tmKey = new Float64Array(8).fill(-1); private tmAt = new Float64Array(8).fill(-99);
  private woOpen = true; private tokBright = true;
  private tmOpen = new Uint8Array(8);

  /** stats for the lab: last redraw cost and how many layers it touched */
  lastRedrawMs = 0; lastLayers = 0; redraws = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 2; this.canvas.height = 2;
    this.gcanvas = document.createElement('canvas');
    this.gcanvas.width = 2; this.gcanvas.height = 2;
    const c = this.canvas.getContext('2d', { alpha: true });
    const g = this.gcanvas.getContext('2d', { alpha: true });
    if (!c || !g) throw new Error('hud: no 2d context');
    this.c = c; this.g = g;
    const t = new THREE.CanvasTexture(this.canvas);
    t.premultiplyAlpha = true;
    t.generateMipmaps = true; // the visor bloom reads mips 2..4
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.colorSpace = THREE.NoColorSpace;
    this.texture = t;
    const gt = new THREE.CanvasTexture(this.gcanvas);
    gt.premultiplyAlpha = true;
    gt.generateMipmaps = false;
    gt.minFilter = THREE.LinearFilter; gt.magFilter = THREE.LinearFilter;
    gt.colorSpace = THREE.NoColorSpace;
    this.glassTexture = gt;
    t.userData.glass = gt;
    t.userData.rings = this.rings;
    for (let i = 0; i < 2; i++) {
      const cv = document.createElement('canvas'); cv.width = 2; cv.height = 2;
      this.spr.push({ cv, ctx: cv.getContext('2d')!, k1: -1, k2: '', lay: -1 });
    }
    const nc = document.createElement('canvas'); nc.width = 1; nc.height = 1; this.gNull = nc.getContext('2d')!;
    for (let i = 0; i < NL; i++) { this.sig.push([]); this.rects.push(new Float32Array(MAXR * 4)); }
    this.measure(); // fallback metrics now; Space Grotesk metrics when it lands
    void loadFont().then(() => { this.measure(); this.forceFull = true; this.layVer++; });
  }

  private measure() {
    const c = this.c;
    for (let wgt = 0; wgt < 2; wgt++) {
      c.font = `${wgt ? W1 : W0} 100px ${FAMILY}, sans-serif`;
      c.letterSpacing = '0px';
      const a = this.adv[wgt];
      for (let i = 32; i < 127; i++) a[i] = c.measureText(String.fromCharCode(i)).width;
      a[0] = a[77]; // fallback: width of 'M'
      if (!(a[48] > 0)) for (let i = 32; i < 127; i++) a[i] = 60; // never zero: zero widths stack digits on one cell
    }
    this.curFont = ''; this.curLS = '';
    this.fontOk = fontReady;
  }

  resize(w: number, h: number, dpr: number) {
    const d = Math.min(2, Math.max(1, dpr));
    const cw = Math.max(2, Math.round(w * d)), ch = Math.max(2, Math.round(h * d));
    this.w = w; this.h = h; this.dpr = d;
    this.u = Math.min(2, Math.max(0.62, Math.min(w / 1600, h / 900)));
    this.kp = Math.max(1, 1.15 * this.u);
    this.hl = Math.max(1, Math.round(this.u * d)) / d;
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw; this.canvas.height = ch;
      this.gcanvas.width = Math.max(2, Math.round(cw * GS)); this.gcanvas.height = Math.max(2, Math.round(ch * GS));
      this.texture.dispose(); this.glassTexture.dispose();
    }
    this.curFont = ''; this.curLS = '';
    this.layout();
    this.forceFull = true;
  }

  // ------------------------------------------------------------------ layout (fixed rects)
  private vx = 0; private vy = 0; private ox = 0; private oy = 0;
  private layout() {
    const u = this.u, W = this.w, H = this.h;
    this.layVer++;
    this.vx = 172 * u; this.vy = H - 164 * u;
    this.ox = W - 172 * u; this.oy = this.vy;
    this.setRect(L_VISOR, 0, 0, 0, 440 * u, 120 * u); this.setRect(L_VISOR, 1, W - 440 * u, 0, 440 * u, 120 * u, true);
    this.setRect(L_CENTER, 0, W / 2 - 320 * u, H / 2 - 250 * u, 640 * u, 540 * u);
    this.setRect(L_ALARM, 0, W / 2 - 360 * u, H - 90 * u, 720 * u, 90 * u);
    this.setRect(L_TOP, 0, W / 2 - 560 * u, 0, 1120 * u, 250 * u);
    this.setRect(L_POSSE, 0, 0, 0, 290 * u, 260 * u);
    this.setRect(L_LOG, 0, W - 470 * u, 0, 470 * u, 300 * u);
    this.setRect(L_VITALS, 0, 0, H - 330 * u, 420 * u, 330 * u);
    this.setRect(L_ORD, 0, W - 560 * u, H - 330 * u, 560 * u, 330 * u); // wide enough for the kit flank
    this.setRect(L_NET, 0, W - 330 * u, 0, 330 * u, 330 * u);
    // callouts under the work order, Pop's radio line above the caution strip, the chat wheel at centre
    this.setRect(L_CALL, 0, W / 2 - 380 * u, 196 * u, 760 * u, 150 * u);
    this.setRect(L_CALL, 1, W / 2 - 460 * u, H - 172 * u, 920 * u, 72 * u);
    this.setRect(L_CALL, 2, W / 2 - 230 * u, H / 2 - 190 * u, 460 * u, 380 * u);
    // the pickup card: stacked over the ordnance cluster (right of the reticle, above the kit's tabs)
    this.setRect(L_PICK, 0, W - 750 * u, H - 480 * u, 440 * u, 190 * u);
  }
  private setRect(L: number, i: number, x: number, y: number, w: number, h: number, keep = false) {
    const r = this.rects[L]; r[i * 4] = x; r[i * 4 + 1] = y; r[i * 4 + 2] = w; r[i * 4 + 3] = h;
    this.nr[L] = keep ? Math.max(this.nr[L], i + 1) : i + 1;
  }
  private addRect(L: number, x: number, y: number, w: number, h: number) {
    const i = this.nr[L];
    if (i >= MAXR) return;
    const r = this.rects[L]; r[i * 4] = x; r[i * 4 + 1] = y; r[i * 4 + 2] = w; r[i * 4 + 3] = h;
    this.nr[L] = i + 1;
  }

  // ------------------------------------------------------------------ signatures
  private begin(L: number) { this.cur = L; this.si = 0; }
  private P(v: number | string) {
    const a = this.sig[this.cur], i = this.si++;
    if (a[i] !== v) { a[i] = v; this.dirty[this.cur] = 1; }
  }
  private q(v: number, step: number) { this.P(Math.round(v / step)); }
  private end() { const a = this.sig[this.cur]; if (a.length !== this.si) { a.length = this.si; this.dirty[this.cur] = 1; } }

  /** shutter progress 0..1 for a condition; records the rising edge */
  private appear(k: number, on: boolean, t: number) {
    if (!on) { this.onT[k] = -1; this.ap[k] = 0; return 0; }
    if (this.onT[k] < 0) this.onT[k] = t;
    const p = Math.min(1, (t - this.onT[k]) / 0.22);
    this.ap[k] = p;
    return p;
  }

  private motion(s: HudState) {
    const t = s.time, dt = Math.min(0.1, Math.max(0, t - this.lastT));
    this.lastT = t;
    const hp = s.hp / Math.max(1, s.hpMax), sh = s.shieldMax > 0 ? s.shield / s.shieldMax : 0;
    // DRAIN: the fill snaps, a ghost of the loss holds 600 ms then drains
    if (hp < this.lastHp - 0.0005) { if (this.ghHull < this.lastHp) this.ghHull = Math.max(this.ghHull, this.lastHp); else this.ghHull = this.lastHp; this.holdHull = t + 0.6; }
    if (hp >= this.ghHull) this.ghHull = hp;
    else if (t > this.holdHull) this.ghHull = Math.max(hp, this.ghHull - dt * Math.max(0.6, (this.ghHull - hp) * 4));
    if (sh < this.lastSh - 0.0005) { this.ghShield = Math.max(this.ghShield, this.lastSh); this.holdSh = t + 0.6; }
    if (sh >= this.ghShield) this.ghShield = sh;
    else if (t > this.holdSh) this.ghShield = Math.max(sh, this.ghShield - dt * Math.max(0.6, (this.ghShield - sh) * 4));
    this.lastHp = hp; this.lastSh = sh;
    if (s.mag !== this.lastMag) { if (this.lastMag >= 0) this.magAt = t; this.lastMag = s.mag; }
    const hn = Math.max(0, Math.ceil(s.hp));
    if (hn !== this.lastHullN) { if (this.lastHullN >= 0) this.hullAt = t; this.lastHullN = hn; }
    const down = s.phase !== 0;
    const htF = s.heat / Math.max(1, s.heatMax);
    const wk = ((s.wave * 16 + s.tier) * 8 + s.waveState) * 4096 + s.enemiesLeft;
    if (wk !== this.woKey) { if (this.woKey >= 0) this.woAt = t; this.woKey = wk; }
    if (s.lives !== this.livesN) { if (this.livesN >= 0) this.livesAt = t; this.livesN = s.lives; }
    this.woOpen = !!s.posseOpen || s.waveState !== 1 || t - this.woAt < 3 || s.enemiesLeft <= 3;
    this.tokBright = t - this.livesAt < 1.6 || s.lives <= 1;
    for (let i = 0; i < s.posse.length && i < 8; i++) {
      const m = s.posse[i];
      const k = m.id * 1e6 + Math.round((m.hp / Math.max(1, m.hpMax)) * 50) * 1000 + Math.round((m.shield / Math.max(1, m.shieldMax)) * 50) * 10 + m.phase;
      if (k !== this.tmKey[i]) { if (this.tmKey[i] >= 0) this.tmAt[i] = t; this.tmKey[i] = k; }
      this.tmOpen[i] = s.posseOpen || m.phase !== 0 || !m.connected || m.paused || t - this.tmAt[i] < 2.5 || m.hp / Math.max(1, m.hpMax) < 0.3 ? 1 : 0;
    }
    this.appear(K_SHIELD, !down && s.shieldMax > 0 && s.shield <= 0.5, t);
    this.appear(K_OVER, !down && htF >= 0.995 && s.heatDeniedAge >= 0.8, t);
    this.appear(K_DENIED, !down && s.heatDeniedAge < 0.8, t);
    this.appear(K_FIRE, !down && s.onFire, t);
    this.appear(K_COACH, !down && s.move !== 3 && s.aimType >= 0, t);
    this.appear(K_TARGET, !down && s.move !== 3 && s.aimType >= 0, t);
    this.appear(K_PRIMED, !down && s.aimPrimed, t);
    this.appear(K_HYPER, !down && s.aimHyper, t);
    this.appear(K_CHAIN, !down && s.chainFrac > 0, t);
    this.appear(K_DOWN, down, t);
    this.appear(K_SKY, s.paused || s.frozen, t);
    this.appear(K_WIPE, s.waveState === 3, t);
    this.appear(K_CLEAR, s.waveState === 2, t);
    this.appear(K_RELOAD, s.reloadFrac >= 0, t);
    this.appear(K_EMPTY, s.mag === 0 && s.reloadFrac < 0, t);
    this.appear(K_LOWHP, hp < 0.3 && !down, t);
    this.appear(K_ABILITY, s.abilityFrac >= 1 && s.abilityName !== '', t);
  }

  private signatures(s: HudState) {
    const t = s.time, down = s.phase !== 0;
    const htF = s.heat / Math.max(1, s.heatMax), hp = s.hp / Math.max(1, s.hpMax);
    const anim = this.animF;

    // VISOR
    this.begin(L_VISOR); this.P(this.w); this.P(this.h); this.end();

    // FLOAT: markers, tags, edge arrows (rects rebuilt each frame)
    this.begin(L_FLOAT);
    const fr = this.rects[L_FLOAT];
    this.nPrev = this.nr[L_FLOAT]; this.prevRects.set(fr.subarray(0, this.nPrev * 4));
    this.nr[L_FLOAT] = 0;
    const u = this.u;
    for (let i = 0; i < s.markers.length; i++) {
      const m = s.markers[i];
      if (!m.onScreen && m.kind === 'primed') continue;
      this.P(m.kind); this.P(m.onScreen ? 1 : 0); this.q(m.sx, 0.5); this.q(m.sy, 0.5); this.q(m.angle, 0.005); this.q(m.dist, 1); this.q(m.t, 0.05);
      if (m.kind === 'downed') this.P(Math.floor(t * 1.8) % 2);
      if (m.kind === 'ping') { this.P(m.label ?? ''); this.P(m.slot ?? 0); }
      if (m.onScreen) this.addRect(L_FLOAT, m.sx - 80 * u, m.sy - 70 * u, 160 * u, 150 * u);
      else { const p = this.edgePos(m.angle); this.addRect(L_FLOAT, p[0] - 110 * u, p[1] - 60 * u, 220 * u, 120 * u); }
    }
    this.P(-1);
    for (let i = 0; i < s.posse.length; i++) {
      const tm = s.posse[i];
      if (!tm.connected) continue;
      this.P(this.tmOpen[i]); this.P(tm.name); this.P(tm.slot); this.P(tm.phase); this.P(tm.paused ? 1 : 0); this.P(tm.onScreen ? 1 : 0);
      this.q(tm.sx, 0.5); this.q(tm.sy, 0.5); this.q(tm.angle, 0.005); this.q(tm.dist, 1); this.q(tm.hp / Math.max(1, tm.hpMax), 0.02);
      if (tm.onScreen) this.addRect(L_FLOAT, tm.sx - 80 * u, tm.sy - 64 * u, 160 * u, 64 * u);
      else { const p = this.edgePos(tm.angle); this.addRect(L_FLOAT, p[0] - 110 * u, p[1] - 60 * u, 220 * u, 120 * u); }
    }
    this.end();

    // CENTER: reticle stack, compass, coaching, alarms, downed / sky cards
    this.begin(L_CENTER);
    this.P(s.phase); this.P(s.move); this.P(s.glyphs);
    this.P(s.aimType); this.P(s.aimPrimed ? 1 : 0); this.P(s.aimHyper ? 1 : 0); this.q(s.aimHp, 0.01); this.q(s.aimShield, 0.01); this.q(s.aimDist, 1);
    this.P(s.primerVerb); this.P(s.detonatorVerb);
    const cue = s.classCue;
    if (cue) { this.P(cue.detReady ? 1 : 0); this.P(cue.detVerb); this.P(cue.detHint); this.P(cue.primeReady ? 1 : 0); this.P(cue.primeHint); this.P(cue.extender); }
    this.q(s.chainFrac, 0.004); this.P(s.chainN); this.q(s.reloadFrac, 0.01); this.q(htF, 0.01);
    this.q(s.hitAge < 0.2 ? s.hitAge : -1, 1 / 60); this.q(s.headAge < 0.3 ? s.headAge : -1, 1 / 60);
    this.q(s.killAge < 1.4 ? s.killAge : -1, 1 / 60); this.q(s.detonateAge < 0.6 ? s.detonateAge : -1, 1 / 60);
    this.P(s.hitKind ?? 0); this.q(s.hitPow ?? 0, 0.1); this.P(s.killHead ? 1 : 0); this.P(s.killBig ? 1 : 0); this.P(s.killN ?? 0);
    const sg = s.sight;
    if (sg) {
      this.P(sg.gun); this.P(sg.ads ? 1 : 0); this.q(sg.spread, 0.002); this.q(sg.track, 0.004); this.P(sg.lock ? 1 : 0);
      if (sg.lock) { this.q(sg.lockX, 0.5); this.q(sg.lockY, 0.5); }
      this.q(sg.spin, 0.02); this.q(sg.steady, 0.02); this.q(sg.bloom, 0.05); this.P(sg.burstHits); this.P(sg.burstRound);
      this.q(sg.overloadAge < 0.4 ? sg.overloadAge : -1, 1 / 60); this.q(sg.charge, 0.02); this.P(sg.chambers); this.P(sg.chamberMax);
    } else this.P(-5);
    for (let i = 0; i < s.hurtDirs.length; i++) { const d = s.hurtDirs[i]; if (d.age < 1) { this.q(d.angle, 0.02); this.q(d.age, 1 / 30); } }
    this.P(-2);
    anim(K_COACH); anim(K_TARGET); anim(K_PRIMED); anim(K_HYPER);
    anim(K_CHAIN); anim(K_DOWN); anim(K_SKY);
    if (down) { this.q(s.bleedOut, 0.05); this.q(s.reviveFrac, 0.005); this.P(s.lives); }
    if (s.paused || s.frozen) { this.P(s.frozen ? 1 : 0); this.q(s.skyT, 0.01); }
    const blink = Math.floor(t * 4.4) % 5 < 3 ? 1 : 0;
    if (s.aimPrimed && !down) this.q(t, 1 / 30);
    else if (down) this.P(blink);
    this.end();

    // ALARM: the master-caution strip
    this.begin(L_ALARM);
    this.P(down ? 1 : 0); anim(K_SHIELD); anim(K_OVER); anim(K_DENIED); anim(K_FIRE);
    this.q(s.heatDeniedAge < 0.2 ? s.heatDeniedAge : -1, 1 / 30); this.q(s.shieldBrokeAge < 0.2 ? s.shieldBrokeAge : -1, 1 / 30);
    if (s.onFire || htF >= 0.995) this.P(blink);
    this.end();

    // TOP: work order
    this.begin(L_TOP);
    this.P(s.wave); this.P(s.waveState); this.P(s.tier); this.P(Math.ceil(s.dirTimer)); this.P(s.enemiesLeft); this.P(s.lives); this.P(this.woOpen ? 1 : 0); this.P(this.tokBright ? 1 : 0);
    anim(K_WIPE); anim(K_CLEAR);
    if (s.waveState === 3) this.P(blink);
    const ob = s.objective;
    if (ob) { this.P(ob.title); this.P(ob.detail); this.P(ob.timer < 0 ? -1 : Math.ceil(ob.timer)); this.P(ob.tone); if (ob.tone === 2) this.P(blink); }
    else this.P(-3);
    const bs = s.boss;
    if (bs) {
      this.P(bs.name); this.P(bs.sub); this.P(bs.count); this.q(bs.hp, 0.002); this.P(bs.hyper ? 1 : 0); this.P(bs.meter); this.P(bs.note);
      this.P(bs.pips.length);
      for (const pp of bs.pips) { this.q(pp.frac, 0.01); this.P(pp.broken ? 1 : 0); this.P(pp.open ? 1 : 0); this.P(pp.label); }
      this.q(bs.crack, 0.01); this.P(bs.warn); this.P(bs.aimPart);
      if (bs.warn || bs.note) this.P(blink);
    } else this.P(-4);
    this.end();

    // POSSE
    this.begin(L_POSSE);
    for (let i = 0; i < s.posse.length; i++) {
      const tm = s.posse[i];
      this.P(this.tmOpen[i]); this.P(tm.id); this.P(tm.name); this.P(tm.slot); this.P(tm.cls); this.q(tm.hp / Math.max(1, tm.hpMax), 0.01); this.q(tm.shield / Math.max(1, tm.shieldMax), 0.01);
      this.q(tm.heat / Math.max(1, tm.heatMax), 0.02); this.P(tm.phase); this.P(tm.paused ? 1 : 0); this.P(tm.connected ? 1 : 0); this.q(tm.reviveFrac, 0.01); this.q(tm.dist, 1);
      if (tm.phase === 1) this.P(Math.floor(t * 2.2) % 2);
    }
    this.end();

    // LOG (notices)
    this.begin(L_LOG);
    this.P(s.showNet ? 1 : 0);
    for (let i = 0; i < s.notices.length; i++) { const n = s.notices[i]; if (n.age < 5.5) { this.P(n.text); this.q(this.noticeAlpha(n.age), 1 / 16); this.P(n.age < 1 ? 1 : 0); } }
    this.end();

    // VITALS: only the ink; the bands are uniforms
    this.begin(L_VITALS);
    this.P(s.cls); this.P(s.className); this.P(Math.max(0, Math.ceil(s.hp))); this.P(s.shieldMax > 0 ? Math.ceil(s.shield) : -1);
    this.P(hp < 0.15 ? 2 : hp < 0.3 ? 1 : 0); this.P(htF >= 0.995 || s.heatDeniedAge < 0.8 ? 3 : htF >= 0.8 ? 2 : htF >= 0.72 ? 4 : htF >= 0.45 ? 1 : 0);
    this.P(s.move); this.P(s.cloaked ? 1 : 0); this.P(s.onFire ? 1 : 0); this.P(down ? 1 : 0);
    if (s.cloaked) this.q(s.classCue?.cloakSecs ?? 0, 0.1);
    this.q(Math.min(0.06, t - this.hullAt), 1 / 60);
    if ((hp < 0.15 && !down) || htF >= 0.995 || s.heatDeniedAge < 0.8 || (s.shieldMax > 0 && s.shield <= 0.5)) this.P(blink);
    this.end();

    // ORDNANCE: only the ink
    this.begin(L_ORD);
    this.P(s.cls); this.P(s.weaponName); this.P(s.mag); this.P(s.magMax); this.P(s.reloadFrac >= 0 ? 1 : 0); this.P(s.glyphs);
    this.P(s.abilityName); this.q(Math.min(0.06, t - this.magAt), 1 / 60);
    const abOpen = s.abilityFrac < 1 || t - this.onT[K_ABILITY] < 2.5 || this.onT[K_ABILITY] < 0;
    this.P(abOpen ? 1 : 0); if (abOpen && s.abilityName) this.q(s.abilityFrac, 0.02);
    anim(K_ABILITY);
    if (s.mag === 0 || (s.mag <= Math.ceil(s.magMax * 0.25))) this.P(blink);
    this.P(s.grenadeName ?? ''); this.P(s.grenades ?? 0); this.P(s.grenadeMax ?? 0); this.q(s.grenadeBank ?? 0, 0.05);
    this.P(s.otherWeaponName ?? ''); this.P(s.specialName ?? ''); this.P(s.specialAmmo ?? 0); this.q(s.specialBank ?? 0, 0.05);
    const pw = s.powerup; this.P(pw ? `${pw.name}|${pw.name2}|${pw.color}|${pw.active ? 1 : 0}|${pw.denied ? 1 : 0}` : ''); this.q(pw?.frac ?? 0, 0.02);
    this.P(s.cards ? s.cards.length : 0);
    const wk = s.whiskey; this.P(wk ? `${wk.n}|${wk.max}|${wk.low ? 1 : 0}|${wk.empty ? 1 : 0}|${wk.chug > 0 ? 1 : 0}` : ''); this.q(wk?.heal ?? 0, 0.02);
    if (wk && wk.low && wk.n > 0 && wk.heal <= 0) this.P(Math.floor(s.time * 2.5) % 2);
    this.P(s.slotIndex ?? 0); this.q(s.chargeFrac ?? -1, 0.04); this.q(s.cookFrac ?? -1, 0.02); if ((s.cookFrac ?? -1) > 0.7) this.P(Math.floor(s.time * 8) % 2);
    this.end();

    // CALL: centre callouts, the quick-chat wheel, Pop's radio line
    this.begin(L_CALL);
    this.P(s.glyphs);
    const cs = s.callouts ?? [];
    for (let i = 0; i < cs.length; i++) { const k = cs[i]; this.P(k.text); this.P(k.sub); this.P(k.tone); this.q(this.callAlpha(k.age), 1 / 20); this.q(Math.min(0.25, k.age), 1 / 60); }
    this.P(-3);
    const wh = s.wheel;
    if (wh?.open) { this.P(1); this.P(wh.sel); } else this.P(0);
    const co = s.coach;
    if (co) { this.P(co.text); this.P(co.glyph); this.q(this.coachAlpha(co.age), 1 / 20); }
    this.end();

    // PICK: the pickup card
    this.begin(L_PICK);
    const pk = s.pickup;
    if (pk) {
      this.P(pk.mode); this.P(pk.title); this.P(pk.tag); this.P(pk.verb); this.P(pk.rest); this.P(pk.glyph); this.P(pk.color); this.P(s.glyphs);
      this.q(Math.min(0.5, pk.age), 1 / 60); this.q(pk.fade, 1 / 20); if (pk.mode === 'near') this.q(pk.dist, 1);
    } else this.P(-6);
    this.end();

    // NET
    this.begin(L_NET);
    this.P(s.showNet ? 1 : 0);
    if (s.showNet) {
      const n = s.net;
      this.P(Math.round(n.ping)); this.q(n.jitter, 0.1); this.q(n.loss, 0.001); this.q(n.cushion, 0.1); this.P(Math.round(n.corrections));
      this.P(Math.round(n.interp)); this.q(n.kbpsIn, 0.1); this.q(n.kbpsOut, 0.1); this.P(Math.round(n.fps)); this.q(n.frameMs, 0.05); this.P(n.draws); this.P(Math.round(n.tris / 100)); this.P(n.tickRate);
    }
    this.end();
  }

  private animF = (k: number) => { const p = this.ap[k]; this.P(p > 0 && p < 1 ? Math.round(p * 30) : p >= 1 ? 31 : -1); };

  private callAlpha(age: number) {
    if (age < 0.12) return age / 0.12;
    if (age > 1.9) return Math.max(0, 1 - (age - 1.9) / 0.6);
    return 1;
  }
  private coachAlpha(age: number) {
    if (age < 0.25) return age / 0.25;
    if (age > 5.8) return Math.max(0, 1 - (age - 5.8) / 0.7);
    return 1;
  }

  private noticeAlpha(age: number) {
    if (age < 0.15) return age / 0.15;
    if (age > 4) return Math.max(0, 1 - (age - 4) / 1.2);
    return 1;
  }

  // ------------------------------------------------------------------ main
  render(s: HudState): boolean {
    this.s = s;
    const d = Math.min(2, Math.max(1, s.dpr));
    if (s.w !== this.w || s.h !== this.h || d !== this.dpr) this.resize(s.w, s.h, s.dpr);
    if (!this.fontOk && fontReady) this.measure();
    if (s.showNet && this.hLast !== s.time) {
      this.hLast = s.time;
      this.hPing[this.hIdx] = s.net.ping; this.hFrame[this.hIdx] = s.net.frameMs;
      this.hIdx = (this.hIdx + 1) % NET_HIST;
    }
    this.motion(s);
    this.pushRings(s);
    this.dirty.fill(0);
    this.signatures(s);
    let full = this.forceFull;
    this.nd = 0;
    let area = 0;
    if (!full) {
      for (let L = 0; L < NL; L++) {
        if (!this.dirty[L]) continue;
        const r = this.rects[L];
        for (let i = 0; i < this.nr[L]; i++) area += this.pushDirty(r[i * 4], r[i * 4 + 1], r[i * 4 + 2], r[i * 4 + 3]);
        if (L === L_FLOAT) for (let i = 0; i < this.nPrev; i++) area += this.pushDirty(this.prevRects[i * 4], this.prevRects[i * 4 + 1], this.prevRects[i * 4 + 2], this.prevRects[i * 4 + 3]);
      }
      if (this.nd === 0) return false;
      if (this.nd >= MAXD || area > this.w * this.h * 0.5) full = true;
    }
    const t0 = performance.now();
    this.redraw(full);
    this.forceFull = false;
    this.lastRedrawMs = performance.now() - t0;
    this.redraws++;
    this.texture.needsUpdate = true;
    this.glassTexture.needsUpdate = true;
    return true;
  }

  private pushDirty(x: number, y: number, w: number, h: number) {
    if (this.nd >= MAXD) return 0;
    const x0 = Math.max(0, Math.floor(x - 2)), y0 = Math.max(0, Math.floor(y - 2));
    const x1 = Math.min(this.w, Math.ceil(x + w + 2)), y1 = Math.min(this.h, Math.ceil(y + h + 2));
    if (x1 <= x0 || y1 <= y0) return 0;
    const i = this.nd++ * 4;
    this.dr[i] = x0; this.dr[i + 1] = y0; this.dr[i + 2] = x1 - x0; this.dr[i + 3] = y1 - y0;
    return (x1 - x0) * (y1 - y0);
  }

  private full = true;
  /** per-element cull inside a layer: does this box touch the region being redrawn? */
  private vis(x: number, y: number, w: number, h: number) {
    if (this.full) return true;
    const d = this.dr;
    for (let j = 0; j < this.nd; j++) {
      const bx = d[j * 4], by = d[j * 4 + 1];
      if (x < bx + d[j * 4 + 2] && bx < x + w && y < by + d[j * 4 + 3] && by < y + h) return true;
    }
    return false;
  }
  private hits(L: number) {
    const r = this.rects[L], n = this.nr[L], d = this.dr;
    for (let i = 0; i < n; i++) {
      const ax = r[i * 4], ay = r[i * 4 + 1], aw = r[i * 4 + 2], ah = r[i * 4 + 3];
      for (let j = 0; j < this.nd; j++) {
        const bx = d[j * 4], by = d[j * 4 + 1];
        if (ax < bx + d[j * 4 + 2] && bx < ax + aw && ay < by + d[j * 4 + 3] && by < ay + ah) return true;
      }
    }
    return false;
  }

  private redraw(full: boolean) {
    const c = this.c, g = this.g, s = this.s, dpr = this.dpr;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.setTransform(dpr * GS, 0, 0, dpr * GS, 0, 0);
    if (full) { c.clearRect(0, 0, this.w, this.h); g.clearRect(0, 0, this.w, this.h); }
    else {
      c.save(); g.save();
      c.beginPath(); g.beginPath();
      const d = this.dr;
      for (let j = 0; j < this.nd; j++) {
        const x = d[j * 4], y = d[j * 4 + 1], w = d[j * 4 + 2], h = d[j * 4 + 3];
        c.clearRect(x, y, w, h); g.clearRect(x, y, w, h);
        c.rect(x, y, w, h); g.rect(x, y, w, h);
      }
      c.clip(); g.clip();
    }
    this.curFont = ''; this.curLS = '';
    c.lineCap = 'butt'; c.lineJoin = 'miter'; c.miterLimit = 4; c.globalAlpha = 1; g.globalAlpha = 1; g.fillStyle = T.glass;
    c.textAlign = 'left'; c.textBaseline = 'alphabetic';
    let n = 0;
    const down = s.phase !== 0;
    for (let L = 0; L < NL; L++) {
      if (!full && !this.hits(L)) continue;
      this.full = full;
      n++;
      switch (L) {
        case L_VISOR: this.drawVisor(); break;
        case L_FLOAT: this.drawMarkers(s); this.drawTags(s); break;
        case L_CENTER:
          if (!down) {
            this.drawHurt(s);
            if (s.move === 3) this.drawFlight(s); else this.drawReticle(s);
            this.drawCoaching(s);
          } else this.drawDowned(s);
          if (s.paused || s.frozen) this.drawSky(s);
          break;
        case L_TOP: this.drawWorkOrder(s); break;
        case L_POSSE: this.drawPosse(s); break;
        case L_LOG: this.drawNotices(s); break;
        case L_VITALS: this.drawVitals(s); break;
        case L_ORD: this.drawOrdnance(s); break;
        case L_NET: if (s.showNet) this.drawNet(s); break;
        case L_ALARM: if (!down) this.drawAlarms(s); break;
        case L_CALL: this.drawCalls(s); break;
        case L_PICK: if (!down && s.pickup) this.drawPickup(s.pickup); break;
      }
      c.globalAlpha = 1; g.globalAlpha = 1;
    }
    if (!full) { c.restore(); g.restore(); }
    this.curFont = ''; this.curLS = '';
    this.lastLayers = n;
  }

  // ------------------------------------------------------------------ primitives
  private font(weight: number, px: number) {
    const key = weight * 10000 + Math.round(px * 4);
    let f = this.fontCache.get(key);
    if (f === undefined) { f = `${weight ? W1 : W0} ${Math.round(px * 4) / 4}px ${FAMILY}, sans-serif`; this.fontCache.set(key, f); }
    if (this.curFont !== f) { this.c.font = f; this.curFont = f; }
  }
  private ls(px: number) {
    const key = Math.round(px * 10);
    let f = this.lsCache.get(key);
    if (f === undefined) { f = `${key / 10}px`; this.lsCache.set(key, f); }
    if (this.curLS !== f) { this.c.letterSpacing = f; this.curLS = f; }
  }
  /** width of a text run (no trailing tracking) */
  private tw(str: string, px: number, weight = 0, track = 0.1) {
    const a = this.adv[weight];
    let w = 0;
    for (let i = 0; i < str.length; i++) { const cc = str.charCodeAt(i); w += cc < 127 ? a[cc] : a[0] * 0.6; }
    return (w * px) / 100 + track * px * Math.max(0, str.length - 1);
  }
  /** text with key; align 0 left, 0.5 centre, 1 right; returns width */
  private txt(str: string, x: number, y: number, px: number, align = 0, color = T.phos, weight = 0, track = 0.1, key = true) {
    const c = this.c;
    this.font(weight, px); this.ls(track * px);
    const w = this.tw(str, px, weight, track);
    const sx = x - w * align;
    void key; // no keyline under type: it's light on glass
    c.fillStyle = color; c.fillText(str, sx, y);
    return w;
  }
  private digitW(px: number, weight = 1) { return (this.adv[weight][48] * px) / 100 * 0.98; }
  /** tabular number in fixed digit cells; returns width */
  private num(v: number, x: number, y: number, px: number, align = 0, color = T.phos, pad = 1, dec = 0, weight = 1, key = true) {
    const c = this.c;
    key = false; // projected light: no keyline under digits
    this.font(weight, px); this.ls(0);
    const neg = v < 0; v = Math.abs(v);
    const scale = dec > 0 ? Math.pow(10, dec) : 1;
    let iv = Math.round(v * scale);
    let nd = 1, tmp = iv;
    while (tmp >= 10) { tmp = Math.floor(tmp / 10); nd++; }
    nd = Math.max(nd, pad + dec);
    const cw = this.digitW(px, weight), dotW = dec > 0 ? cw * 0.45 : 0;
    const w = nd * cw + dotW;
    let cx = x - w * align + w;
    if (key) { const sm = px < 15 * this.u; c.lineJoin = 'round'; c.lineWidth = this.kp * (sm ? 2.6 : 2) + px * (sm ? 0.06 : 0.04); c.strokeStyle = sm ? T.keyHard : T.key; }
    c.fillStyle = color;
    for (let i = 0; i < nd; i++) {
      if (dec > 0 && i === dec) { cx -= dotW; const dx = cx + dotW * 0.5 - px * 0.06; if (key) c.fillStyle = T.key, c.fillRect(dx - this.kp, y - px * 0.13 - this.kp, px * 0.12 + 2 * this.kp, px * 0.13 + 2 * this.kp); c.fillStyle = color; c.fillRect(dx, y - px * 0.13, px * 0.12, px * 0.13); }
      cx -= cw;
      const dg = iv % 10; iv = Math.floor(iv / 10);
      const ch = DIG[dg];
      const off = (cw - (this.adv[weight][48 + dg] * px) / 100) * 0.5;
      if (key) c.strokeText(ch, cx + off, y);
      c.fillText(ch, cx + off, y);
    }
    void neg;
    c.lineJoin = 'miter';
    return w;
  }
  /** stroke the current path: key underneath, then the light */
  private ks(lw: number, color: string, key = true) {
    const c = this.c;
    void key; // projected light: no dark key under strokes
    c.lineWidth = lw * LINE; c.strokeStyle = color; c.stroke();
  }
  /** a hard key under the light, for marks that must hold on white */
  private kh(lw: number, color: string) {
    const c = this.c;
    c.lineWidth = lw * LINE; c.strokeStyle = color; c.stroke();
  }
  private arc(x: number, y: number, r: number, a0: number, a1: number, lw: number, color: string, key = true, ccw = false) {
    const c = this.c;
    c.beginPath(); c.arc(x, y, r, a0, a1, ccw); this.ks(lw, color, key);
  }
  /** gauge angle for a fraction; mirrored instruments run counter-clockwise from 4:30 */
  private ga(f: number, mir: boolean) { const a = A0 + SW * f; return mir ? Math.PI - a : a; }
  private ringPath(x: number, y: number, r: number, f0: number, f1: number, mir: boolean) {
    const c = this.c, a0 = this.ga(f0, mir), a1 = this.ga(f1, mir);
    c.moveTo(x + Math.cos(a0) * r, y + Math.sin(a0) * r);
    c.arc(x, y, r, a0, a1, mir);
  }
  private ringTicks(x: number, y: number, r0: number, n: number, major: number, len: number, lenMajor: number, mir: boolean, col: string, lw: number) {
    const c = this.c;
    c.beginPath();
    for (let i = 0; i <= n; i++) {
      const a = this.ga(i / n, mir), l = i % major === 0 ? lenMajor : len;
      const ca = Math.cos(a), sa = Math.sin(a);
      c.moveTo(x + ca * r0, y + sa * r0); c.lineTo(x + ca * (r0 + l), y + sa * (r0 + l));
    }
    this.ks(lw, col);
  }
  /** chamfered plate: 45-degree cuts on the top-left and bottom-right corners (the house shape) */
  private chamfer(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, k: number, all = false) {
    ctx.beginPath();
    ctx.moveTo(x + k, y); ctx.lineTo(x + w - (all ? k : 0), y);
    if (all) ctx.lineTo(x + w, y + k);
    ctx.lineTo(x + w, y + h - k); ctx.lineTo(x + w - k, y + h);
    ctx.lineTo(x + (all ? k : 0), y + h);
    if (all) ctx.lineTo(x, y + h - k);
    ctx.lineTo(x, y + k); ctx.closePath();
  }
  /** a glass seat: mask in the glass canvas + one hairline bevel and bright chamfer corners */
  private seat(x: number, y: number, w: number, h: number, k: number, density = 1, bevel = T.phos26, all = false) {
    const g = this.g, c = this.c;
    g.globalAlpha = density; this.chamfer(g, x, y, w, h, k, all); g.fill(); g.globalAlpha = 1;
    this.chamfer(c, x + 0.5 * this.hl, y + 0.5 * this.hl, w - this.hl, h - this.hl, k, all);
    c.lineWidth = this.hl; c.strokeStyle = bevel; c.stroke();
    // the two cut corners catch the light
    c.beginPath(); c.moveTo(x, y + k + 4 * this.u); c.lineTo(x, y + k); c.lineTo(x + k, y); c.lineTo(x + k + 4 * this.u, y);
    c.moveTo(x + w, y + h - k - 4 * this.u); c.lineTo(x + w, y + h - k); c.lineTo(x + w - k, y + h); c.lineTo(x + w - k - 4 * this.u, y + h);
    c.lineWidth = this.hl * 1.5; c.strokeStyle = T.phos60; c.stroke();
  }
  /** a round glass lens: denser toward its rim (the glass has thickness), plus the light it catches */
  private disc(x: number, y: number, r: number, density = 1, sheen = true) {
    const g = this.g;
    if (!this.lensGrad) {
      const lg = g.createRadialGradient(0, 0, 0, 0, 0, 1);
      lg.addColorStop(0, 'rgba(255,255,255,0.4)'); lg.addColorStop(0.55, 'rgba(255,255,255,0.5)'); lg.addColorStop(0.86, 'rgba(255,255,255,0.78)'); lg.addColorStop(1, 'rgba(255,255,255,0.95)');
      this.lensGrad = lg;
    }
    g.save(); g.globalAlpha = density; g.translate(x, y); g.scale(r, r);
    g.beginPath(); g.arc(0, 0, 1, 0, TAU); g.fillStyle = this.lensGrad; g.fill(); g.restore();
    g.fillStyle = T.glass;
    if (sheen) {
      const c = this.c;
      c.beginPath(); c.arc(x, y, r - this.hl * 1.5, Math.PI * 1.06, Math.PI * 1.42);
      c.lineWidth = this.hl * 1.5; c.strokeStyle = T.phos42; c.stroke();
      c.beginPath(); c.arc(x, y, r - this.hl * 1.5, Math.PI * 1.9, Math.PI * 1.98);
      c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    }
  }
  private lensGrad: CanvasGradient | null = null;
  private diamond(x: number, y: number, r: number) {
    const c = this.c; c.moveTo(x, y - r); c.lineTo(x + r, y); c.lineTo(x, y + r); c.lineTo(x - r, y); c.closePath();
  }
  private ease(p: number) { const q = 1 - p; return 1 - q * q * q * q; }

  /** solid alarm plate with stencil text and a shutter-in; centred at x, top y. returns height */
  private alarm(str: string, x: number, y: number, size: number, p: number, fill = T.plateRed, edge = T.plateRedEdge, flash = 0) {
    const u = this.u, c = this.c;
    const tw = this.tw(str, size, 1, 0.16);
    const ph = size * 2.0, pw = tw + size * 2.8, k = ph * 0.32;
    const e = this.ease(Math.min(1, p / 0.8));
    const w = pw * e;
    const px = x - w / 2;
    // the outer register line, then the plate
    c.globalAlpha = Math.min(1, p * 2);
    this.chamfer(c, px - 3 * u, y - 3 * u, w + 6 * u, ph + 6 * u, k + 2 * u);
    c.lineWidth = this.kp * 2 + this.hl; c.strokeStyle = T.key; c.stroke();
    c.lineWidth = this.hl; c.strokeStyle = edge; c.stroke();
    this.chamfer(c, px, y, w, ph, k);
    c.fillStyle = fill; c.fill();
    if (flash > 0) { c.globalAlpha = flash; c.fillStyle = 'rgba(255,240,226,0.95)'; c.fill(); }
    // hazard ticks at the plate ends
    if (e > 0.9) {
      c.globalAlpha = 0.55;
      c.beginPath();
      for (let i = 0; i < 3; i++) {
        const ox = px + k + 5 * u + i * 5 * u;
        c.moveTo(ox, y + ph - 4 * u); c.lineTo(ox + 4 * u, y + 4 * u);
        const rx = px + w - k - 5 * u - i * 5 * u;
        c.moveTo(rx, y + 4 * u); c.lineTo(rx - 4 * u, y + ph - 4 * u);
      }
      c.lineWidth = 1.4 * u; c.strokeStyle = T.plateText; c.stroke();
    }
    const ta = Math.max(0, (p - 0.55) / 0.45);
    if (ta > 0) { c.globalAlpha = ta; this.txt(str, x, y + ph / 2 + size * 0.36, size, 0.5, T.plateText, 1, 0.16, false); }
    c.globalAlpha = 1;
    return ph;
  }

  /** button glyph: key cap (kbm) or PS5 symbol (pad). returns width */
  private glyph(id: string, x: number, y: number, size: number, color = T.phos) {
    const c = this.c, u = this.u, r = size * 0.6;
    const pad = this.s.glyphs === 'pad';
    if (pad && (id === '✕' || id === '○' || id === '□' || id === '△')) {
      c.beginPath(); c.arc(x + r, y, r, 0, TAU); this.ks(this.hl * 1.4, T.phos60);
      c.beginPath();
      const gg = r * 0.46;
      if (id === '✕') { c.moveTo(x + r - gg, y - gg); c.lineTo(x + r + gg, y + gg); c.moveTo(x + r + gg, y - gg); c.lineTo(x + r - gg, y + gg); }
      else if (id === '○') c.arc(x + r, y, gg, 0, TAU);
      else if (id === '□') c.rect(x + r - gg * 0.9, y - gg * 0.9, gg * 1.8, gg * 1.8);
      else { c.moveTo(x + r, y - gg); c.lineTo(x + r + gg, y + gg * 0.75); c.lineTo(x + r - gg, y + gg * 0.75); c.closePath(); }
      this.ks(1.6 * u, color);
      return r * 2;
    }
    if (id === 'LMB' || id === 'RMB') {
      const mw = size * 0.8, mh = size * 1.15;
      c.beginPath(); c.moveTo(x, y - mh * 0.15); c.arcTo(x, y - mh / 2, x + mw / 2, y - mh / 2, mw / 2); c.arcTo(x + mw, y - mh / 2, x + mw, y, mw / 2);
      c.lineTo(x + mw, y + mh * 0.2); c.arcTo(x + mw, y + mh / 2, x + mw / 2, y + mh / 2, mw / 2); c.arcTo(x, y + mh / 2, x, y, mw / 2); c.closePath();
      this.ks(this.hl * 1.4, T.phos60);
      const lx = id === 'LMB' ? x + 1.5 * u : x + mw / 2 + 1.5 * u;
      c.fillStyle = color; c.fillRect(lx, y - mh * 0.38, mw / 2 - 3 * u, mh * 0.22);
      return mw;
    }
    // key cap in the house shape
    const kw = Math.max(size * 1.2, this.tw(id, size * 0.66, 1, 0.05) + size * 0.6), kh = size * 1.2, ch = 3.5 * u;
    this.chamfer(c, x, y - kh / 2, kw, kh, ch);
    c.fillStyle = 'rgba(2,9,10,0.35)'; c.fill();
    this.ks(this.hl * 1.4, T.phos80);
    const dir = id === '←' ? Math.PI : id === '→' ? 0 : id === '↑' ? -Math.PI / 2 : id === '↓' ? Math.PI / 2 : -9;
    if (dir > -9) {
      // the D-pad: a drawn arrow with a real head (the font's arrow reads as a dash at HUD sizes)
      const cx = x + kw / 2, ca = Math.cos(dir), sa = Math.sin(dir), L = size * 0.3, hh = size * 0.2;
      c.beginPath();
      c.moveTo(cx - ca * L, y - sa * L); c.lineTo(cx + ca * L, y + sa * L);
      c.moveTo(cx + ca * L - ca * hh - sa * hh, y + sa * L - sa * hh + ca * hh); c.lineTo(cx + ca * L, y + sa * L);
      c.lineTo(cx + ca * L - ca * hh + sa * hh, y + sa * L - sa * hh - ca * hh);
      c.lineWidth = Math.max(1.6 * u, size * 0.11); c.strokeStyle = color; c.lineCap = 'round'; c.lineJoin = 'round'; c.stroke();
      c.lineCap = 'butt'; c.lineJoin = 'miter';
      return kw;
    }
    this.txt(id, x + kw / 2, y + size * 0.24, size * 0.66, 0.5, color, 1, 0.05, false);
    return kw;
  }
  private bind(b: { kbm: string; pad: string }) { return this.s.glyphs === 'pad' ? b.pad : b.kbm; }

  // ------------------------------------------------------------------ visor (helmet felt, never drawn)
  private drawVisor() {
    const c = this.c, u = this.u, W = this.w;
    // two hairline arcs hugging the top corners: the edge of the glass catching light
    const R = 1500 * u;
    c.lineWidth = this.hl; c.strokeStyle = 'rgba(228,255,244,0.14)';
    c.beginPath(); c.arc(W / 2, R + 30 * u, R, Math.PI + 0.02, Math.PI + 0.36); c.stroke();
    c.beginPath(); c.arc(W / 2, R + 30 * u, R, -0.36, -0.02); c.stroke();
    // registration ticks at their ends
    c.strokeStyle = 'rgba(228,255,244,0.3)';
    c.beginPath();
    for (let i = 0; i < 2; i++) {
      const a = i ? -0.36 : Math.PI + 0.36, cx = W / 2 + Math.cos(a) * R, cy = R + 30 * u + Math.sin(a) * R;
      const nx = Math.cos(a), ny = Math.sin(a);
      c.moveTo(cx, cy); c.lineTo(cx - nx * 8 * u, cy - ny * 8 * u);
    }
    c.stroke();
  }

  // ------------------------------------------------------------------ LIFE (bottom-left)
  // The gauge bands (shield, hull, heat band + needle) are SDF arcs in the post pass, fed by
  // this.rings: a value change costs no canvas work. The canvas holds a cached STATIC sprite
  // (lens sheen, bezel, scales, engraving, data plate) plus the dynamic ink (counts, chips, flank).
  private drawVitals(s: HudState) {
    const u = this.u, c = this.c, x = this.vx, y = this.vy;
    const down = s.phase !== 0;
    const htF = Math.min(1, s.heat / Math.max(1, s.heatMax));
    const hpF = Math.max(0, s.hp / Math.max(1, s.hpMax));
    const offline = s.shieldMax > 0 && s.shield <= 0.5;
    const blinkR = Math.floor(s.time * 4.4) % 5 < 3 ? 1 : 0.35;
    // glass (every redraw: the glass canvas is cleared with the light)
    this.disc(x, y, 116 * u, 1, false);
    this.plateGlass(x, y + 92 * u, s.className || 'CONTRACTOR');
    const hot = htF >= 0.8 ? 1 : 0;
    this.sprite(0, (down ? 1 : 0) + hot * 2 + (s.cls & 3) * 4, s.className, x - 124 * u, y - 124 * u, 248 * u, 268 * u);
    c.globalAlpha = down ? 0.5 : 1;
    const crit = hpF < 0.15, low = hpF < 0.3;
    const t = s.time, age = t - this.hullAt, flick = age < 0.016 ? 0.25 : age < 0.05 ? 0.6 : 1;
    const ga0 = c.globalAlpha;
    c.globalAlpha = ga0 * flick;
    this.num(Math.max(0, Math.ceil(s.hp)), x, y + 13 * u, 38 * u, 0.5, crit ? (blinkR > 0.5 ? T.red : T.red60) : low ? T.amber : T.phos, 1);
    c.globalAlpha = ga0;
    if (s.shieldMax > 0) {
      if (offline) this.lampChip('SHLD OFF', x, y - 28 * u, T.plateText, blinkR > 0.5 ? T.plateRed : 'rgba(150,24,18,0.9)');
      else {
        const nw = this.num(Math.ceil(s.shield), x + 5 * u, y - 23 * u, 13 * u, 0.5, T.shield, 1);
        this.shieldGlyph(x - nw / 2 - 3 * u, y - 28.5 * u, 4.5 * u);
      }
    }
    const over = htF >= 0.995 || s.heatDeniedAge < 0.8;
    const lamp = over ? 'OVR' : htF >= 0.72 ? 'HOT' : htF >= 0.45 ? 'WRM' : 'RDY';
    const lampCol = over ? (blinkR > 0.5 ? T.red : T.red60) : lamp === 'HOT' ? T.red : lamp === 'WRM' ? T.amber : T.shield60;
    this.lampChip(lamp, x, y + 46 * u, lampCol);
    // flank lamps (Tier B): flight mode, dodge, cloak, pack DoT, on fire
    let fy = y - 74 * u;
    const fx = x + 128 * u;
    if (s.move === 2) fy += this.flank('HOVER', fx, fy, T.phos);
    if (s.move === 3) fy += this.flank('FLIGHT', fx, fy, T.phos);
    if (s.move === 4) fy += this.flank('DODGE', fx, fy, T.phos);
    if (s.cloaked) fy += this.flank(s.classCue ? `CLOAK ${Math.max(0.1, s.classCue.cloakSecs).toFixed(1)}` : 'CLOAK', fx, fy, T.shield);
    if (htF >= 0.8 && htF < 0.995) fy += this.flank('PACK DOT', fx, fy, T.red);
    if (s.onFire) fy += this.flank('BURN', fx, fy, T.red);
    else if (s.burning) fy += this.flank('BURNING', fx, fy, T.red);
    if (s.acid) fy += this.flank('ACID', fx, fy, T.phos);
    if (s.wet) fy += this.flank('WET', fx, fy, T.shield);
    c.globalAlpha = 1;
  }
  private staticVitals() {
    const s = this.s, u = this.u, c = this.c, x = this.vx, y = this.vy;
    const htF = Math.min(1, s.heat / Math.max(1, s.heatMax));
    c.globalAlpha = s.phase !== 0 ? 0.5 : 1;
    this.disc(x, y, 116 * u, 1, true);
    c.beginPath(); c.arc(x, y, 116 * u - this.hl / 2, 0, TAU); c.lineWidth = this.hl; c.strokeStyle = T.phos14; c.stroke();
    this.ringTicks(x, y, 104 * u, 40, 10, 3 * u, 7 * u, false, T.phos42, this.hl);
    c.beginPath(); this.ringPath(x, y, 103 * u, 0, 1, false); this.ks(this.hl, T.phos26);
    this.engrave('LIFE', x, y - 108 * u);
    // hull frame hairline
    c.beginPath(); this.ringPath(x, y, 79 * u - 6 * u - 3 * u, 0, 1, false); c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    // thermal scale r61: minors, majors at 0.45 WARM / 0.72 CRIT / 0.80 REDLINE / 1.0, hatch past redline
    const rT = 61 * u;
    c.beginPath(); this.ringPath(x, y, rT, 0, 1, false); c.lineWidth = this.hl; c.strokeStyle = T.phos42; c.stroke();
    c.beginPath();
    for (let i = 0; i <= 20; i++) { const a = this.ga(i / 20, false); c.moveTo(x + Math.cos(a) * rT, y + Math.sin(a) * rT); c.lineTo(x + Math.cos(a) * (rT - 2.5 * u), y + Math.sin(a) * (rT - 2.5 * u)); }
    c.lineWidth = this.hl; c.strokeStyle = T.phos42; c.stroke();
    c.beginPath();
    for (let i = 0; i < MAJ.length; i++) { const a = this.ga(MAJ[i], false); c.moveTo(x + Math.cos(a) * (rT + 1 * u), y + Math.sin(a) * (rT + 1 * u)); c.lineTo(x + Math.cos(a) * (rT - 6 * u), y + Math.sin(a) * (rT - 6 * u)); }
    this.ks(this.hl * 1.5, T.phos80);
    c.beginPath();
    for (let i = 0; i <= 8; i++) {
      const a = this.ga(0.8 + i * 0.025, false), b = this.ga(0.8 + i * 0.025 + 0.012, false);
      c.moveTo(x + Math.cos(a) * (rT - 2 * u), y + Math.sin(a) * (rT - 2 * u)); c.lineTo(x + Math.cos(b) * (rT - 6 * u), y + Math.sin(b) * (rT - 6 * u));
    }
    c.lineWidth = this.hl; c.strokeStyle = htF >= 0.8 ? T.red60 : T.red25; c.stroke();
    this.txt('HULL', x, y + 30 * u, 11 * u, 0.5, T.phos80, 1, 0.24);
    this.dataPlate(x, y + 92 * u, s.className || 'CONTRACTOR', VTL_SN[s.cls & 3]);
    c.globalAlpha = 1;
  }
  /** write the gauge-band uniforms (no canvas work) */
  private pushRings(s: HudState) {
    const R = this.rings, down = s.phase !== 0;
    const blink = Math.floor(s.time * 4.4) % 5 < 3 ? 1 : 0;
    const hpF = Math.max(0, s.hp / Math.max(1, s.hpMax)), htF = Math.min(1, s.heat / Math.max(1, s.heatMax));
    R[0] = this.w; R[1] = this.h; R[2] = this.u; R[3] = blink;
    R[4] = this.vx; R[5] = this.vy;
    R[6] = s.shieldMax > 0 ? Math.max(0, s.shield / s.shieldMax) : 0; R[7] = this.ghShield;
    R[8] = hpF; R[9] = this.ghHull; R[10] = htF;
    R[11] = hpF < 0.15 ? (blink ? 2 : 3) : hpF < 0.3 ? 1 : 0;
    R[12] = s.shieldMax <= 0 ? 3 : s.shield <= 0.5 ? 1 : 0;
    const over = htF >= 0.995 || s.heatDeniedAge < 0.8;
    R[13] = over ? (blink ? 3 : 4) : htF >= 0.8 ? 2 : htF >= 0.45 ? 1 : 0;
    R[14] = down ? 0.5 : 1;
    R[16] = this.ox; R[17] = this.oy;
    R[18] = s.mag; R[19] = Math.max(1, s.magMax); R[20] = s.reloadFrac;
    R[21] = s.abilityName ? Math.max(0, Math.min(1, s.abilityFrac)) : -1;
    const n = Math.max(1, s.magMax), low = s.mag > 0 && s.mag <= Math.max(1, Math.ceil(n * 0.25));
    R[22] = s.mag === 0 ? (blink ? 3 : 4) : low ? (blink ? 1 : 2) : 0;
    R[23] = down ? 0.5 : 1;
    R[15] = s.shieldMax > 0 && s.shieldBrokeAge >= 0 && s.shieldBrokeAge < 0.28 ? 1 - s.shieldBrokeAge / 0.28 : 0;
  }
  /** glass under a data plate (the light half lives in the static sprite) */
  private plateGlass(x: number, y: number, label: string) {
    const u = this.u, w = Math.max(116 * u, this.tw(label, 12.5 * u, 1, 0.14) + 34 * u), h = 22 * u;
    this.chamfer(this.g, x - w / 2, y - h / 2, w, h, 6 * u); this.g.fill();
  }
  /** cached static sprite i; rebuilt only when (k1,k2) or the layout changes */
  private sprite(i: number, k1: number, k2: string, x0: number, y0: number, w: number, h: number) {
    const sp = this.spr[i];
    if (sp.k1 !== k1 || sp.k2 !== k2 || sp.lay !== this.layVer) {
      const dpr = this.dpr, cw = Math.ceil(w * dpr), ch = Math.ceil(h * dpr);
      if (sp.cv.width !== cw || sp.cv.height !== ch) { sp.cv.width = cw; sp.cv.height = ch; }
      const sc = sp.ctx;
      sc.setTransform(1, 0, 0, 1, 0, 0); sc.clearRect(0, 0, cw, ch);
      sc.setTransform(dpr, 0, 0, dpr, -x0 * dpr, -y0 * dpr);
      sc.textAlign = 'left'; sc.textBaseline = 'alphabetic'; sc.lineCap = 'butt'; sc.lineJoin = 'miter';
      const c0 = this.c, g0 = this.g, f0 = this.curFont, l0 = this.curLS;
      this.c = sc; this.g = this.gNull; this.curFont = ''; this.curLS = '';
      if (i === 0) this.staticVitals(); else this.staticOrd();
      this.c = c0; this.g = g0; this.curFont = f0; this.curLS = l0;
      // restore what the static draw may have changed on the main context's cache
      this.c.font = f0 || this.c.font;
      sp.k1 = k1; sp.k2 = k2; sp.lay = this.layVer;
      this.spriteBuilds++;
    }
    this.c.drawImage(sp.cv, x0, y0, w, h);
  }
  private headCap(x: number, y: number, r: number, f: number, mir: boolean, w: number, col: string) {
    const c = this.c, a = this.ga(f, mir), ca = Math.cos(a), sa = Math.sin(a);
    c.beginPath(); c.moveTo(x + ca * (r - w * 0.9), y + sa * (r - w * 0.9)); c.lineTo(x + ca * (r + w * 0.9), y + sa * (r + w * 0.9));
    c.lineWidth = 1.6 * this.u; c.strokeStyle = col; c.stroke();
  }
  private shieldGlyph(x: number, y: number, r: number) {
    const c = this.c;
    c.beginPath(); c.moveTo(x - r, y - r); c.lineTo(x + r, y - r); c.lineTo(x + r, y + r * 0.2); c.lineTo(x, y + r * 1.25); c.lineTo(x - r, y + r * 0.2); c.closePath();
    this.ks(this.hl * 1.4, T.shield);
  }
  private engrave(str: string, x: number, y: number) {
    const u = this.u, c = this.c;
    const w = this.tw(str, 12 * u, 1, 0.3);
    this.g.globalAlpha = 1;
    c.beginPath(); c.moveTo(x - w / 2 - 12 * u, y - 3 * u); c.lineTo(x - w / 2 - 5 * u, y - 3 * u); c.moveTo(x + w / 2 + 5 * u, y - 3 * u); c.lineTo(x + w / 2 + 12 * u, y - 3 * u);
    c.lineWidth = this.hl; c.strokeStyle = T.phos42; c.stroke();
    this.txt(str, x, y, 12 * u, 0.5, T.phos80, 1, 0.3, true);
  }
  private lampChip(str: string, x: number, y: number, col: string, fill = '') {
    const u = this.u, c = this.c;
    const w = this.tw(str, 11.5 * u, 1, 0.16) + 12 * u, h = 16 * u;
    this.chamfer(c, x - w / 2, y - h / 2, w, h, 3.5 * u);
    if (fill) { c.fillStyle = fill; c.fill(); } else { c.fillStyle = T.tabFill; c.fill(); c.lineWidth = this.hl * 1.4; c.strokeStyle = col; c.stroke(); }
    this.txt(str, x, y + 4.2 * u, 11.5 * u, 0.5, col, 1, 0.16, !fill);
  }
  /** the instrument's name plate in the ring gap, with the part number under it */
  private dataPlate(x: number, y: number, label: string, serial: string) {
    const u = this.u, c = this.c;
    const size = 12.5 * u;
    const w = Math.max(116 * u, this.tw(label, size, 1, 0.14) + 34 * u), h = 22 * u;
    this.seat(x - w / 2, y - h / 2, w, h, 6 * u, 1, T.phos42);
    this.txt(label, x, y + size * 0.36, size, 0.5, T.phos, 1, 0.14);
    // rails from the plate up into the ring's open ends
    c.beginPath();
    c.moveTo(x - w / 2, y); c.lineTo(x - w / 2 - 14 * u, y); c.lineTo(x - w / 2 - 24 * u, y - 10 * u);
    c.moveTo(x + w / 2, y); c.lineTo(x + w / 2 + 14 * u, y); c.lineTo(x + w / 2 + 24 * u, y - 10 * u);
    this.ks(this.hl, T.phos42);
    void serial; // part numbers dropped: text on nothing under the chroma split
  }
  private flank(str: string, x: number, y: number, col: string) {
    const u = this.u, c = this.c, size = 9 * u;
    const w = this.tw(str, size, 1, 0.18) + 22 * u, h = 17 * u;
    this.seat(x, y, w, h, 4 * u, 0.9, T.phos26);
    c.fillStyle = col; c.fillRect(x + 6 * u, y + h / 2 - 2.5 * u, 5 * u, 5 * u);
    this.txt(str, x + 15 * u, y + h / 2 + size * 0.36, size, 0, col, 1, 0.18, false);
    return h + 5 * u;
  }

  // ------------------------------------------------------------------ ORDNANCE (bottom-right, the mirror)
  private drawOrdnance(s: HudState) {
    const u = this.u, c = this.c, x = this.ox, y = this.oy, t = s.time;
    const down = s.phase !== 0;
    this.disc(x, y, 116 * u, 1, false);
    this.plateGlass(x, y + 92 * u, s.weaponName || 'SIDEARM');
    this.sprite(1, (down ? 1 : 0) + (s.cls & 3) * 2 + Math.max(1, s.magMax) * 8, s.weaponName, x - 124 * u, y - 124 * u, 248 * u, 268 * u);
    c.globalAlpha = down ? 0.5 : 1;
    const n = Math.max(1, s.magMax);
    const reloading = s.reloadFrac >= 0;
    const low = s.mag > 0 && s.mag <= Math.max(1, Math.ceil(n * 0.25));
    const blink = Math.floor(t * 4.4) % 5 < 3;
    const live = low ? (blink ? T.amber : T.amber60) : T.phos;
    const age = t - this.magAt, flick = age < 0.016 ? 0.25 : age < 0.05 ? 0.6 : 1;
    const ga0 = c.globalAlpha;
    c.globalAlpha = ga0 * flick;
    const empty = s.mag === 0 && !reloading;
    this.num(s.mag, x, y + 13 * u, 38 * u, 0.5, empty ? (blink ? T.red : T.red60) : low ? live : T.phos, 1);
    c.globalAlpha = ga0;
    // the chip only speaks when it adds something (RDS duplicated RNDS)
    if (reloading) this.lampChip('RLD', x, y + 46 * u, T.shield);
    else if (empty) this.lampChip('DRY', x, y + 46 * u, blink ? T.red : T.red60);
    else if (low) this.lampChip('LOW', x, y + 46 * u, T.amber);
    // ability flank: full while charging and for 2.5 s after it comes ready; then it settles to a quiet
    // glyph + name (round 2, James couldn't find the Juggernaut's charge: the move's name and button stay on screen)
    if (s.abilityName) {
      const af = Math.max(0, Math.min(1, s.abilityFrac));
      const rdy = af >= 1, open = !rdy || t - this.onT[K_ABILITY] < 2.5 || this.onT[K_ABILITY] < 0;
      const size = 9.5 * u, gid = this.bind(BIND.ability);
      const w = this.tw(s.abilityName, size, 1, 0.16) + 46 * u, h = 22 * u;
      const fx = x - 128 * u - w, fy = y - 82 * u;
      this.seat(fx, fy, w, h, 5 * u, 1, open ? (rdy ? T.amber60 : T.phos26) : T.phos14);
      const gw = this.glyph(gid, fx + 6 * u, fy + h / 2, 13 * u, rdy ? T.amber : T.phos60);
      this.txt(s.abilityName, fx + 12 * u + gw, fy + h / 2 + size * 0.36, size, 0, open ? (rdy ? T.amber : T.phos60) : T.phos60, 1, 0.16, false);
      if (open) {
        c.beginPath(); c.moveTo(fx + 6 * u, fy + h + 4 * u); c.lineTo(fx + w - 6 * u, fy + h + 4 * u); c.lineWidth = 2 * u; c.strokeStyle = T.phos14; c.stroke();
        c.beginPath(); c.moveTo(fx + 6 * u, fy + h + 4 * u); c.lineTo(fx + 6 * u + (w - 12 * u) * af, fy + h + 4 * u); c.strokeStyle = rdy ? T.amber : T.phos60; c.stroke();
      }
      const pa = this.ap[K_ABILITY];
      if (pa > 0 && pa < 1) { c.globalAlpha = 1 - pa; this.chamfer(c, fx - 4 * u * pa, fy - 4 * u * pa, w + 8 * u * pa, h + 8 * u * pa, 5 * u); c.lineWidth = 2 * u; c.strokeStyle = T.amber; c.stroke(); c.globalAlpha = ga0; }
    }
    if (empty || low) {
      const gid = this.bind(BIND.reload), size = 9.5 * u;
      const w = this.tw('RELOAD', size, 1, 0.16) + 46 * u, h = 22 * u;
      const fx = x - 128 * u - w, fy = y - 50 * u;
      this.seat(fx, fy, w, h, 5 * u, 1, empty ? T.red60 : T.amber60);
      const col = empty ? T.red : T.amber;
      const gw = this.glyph(gid, fx + 6 * u, fy + h / 2, 13 * u, col);
      this.txt('RELOAD', fx + 12 * u + gw, fy + h / 2 + size * 0.36, size, 0, col, 1, 0.16, false);
    }
    // charge-up (Duel Blaster): an arc inside the round counter
    if ((s.chargeFrac ?? -1) >= 0) {
      const cf = s.chargeFrac!, full = cf >= 1;
      c.beginPath(); c.arc(x, y, 70 * u, -Math.PI / 2, -Math.PI / 2 + TAU * cf); c.lineWidth = 4 * u; c.strokeStyle = full ? T.amber : T.phos80; c.stroke();
    }
    // cooking a fused grenade: the fuse burns round the disc, amber then red, blinking at the end
    if ((s.cookFrac ?? -1) >= 0) {
      const k = s.cookFrac!, hot = k > 0.7, on = !hot || Math.floor(t * 8) % 2 === 0;
      c.beginPath(); c.arc(x, y, 92 * u, -Math.PI / 2, -Math.PI / 2 + TAU * (1 - k), false);
      c.lineWidth = 5 * u; c.strokeStyle = hot ? (on ? T.red : T.red60) : T.amber; c.stroke();
      if (on) this.lampChip(hot ? 'LET GO' : 'COOKING', x, y - 50 * u, hot ? T.red : T.amber);
    }
    // the rest of the kit, flanking the disc below ability / reload: grenades, the other gun, the crate special
    let ky = y - 18 * u;
    if (s.grenadeName) {
      const n = s.grenades ?? 0, max = s.grenadeMax ?? 3;
      ky += this.kitRow(x, ky, BIND.grenade, s.grenadeName, n > 0 ? T.phos : T.phos42, (cx, cy) => {
        for (let i = 0; i < max; i++) {
          c.beginPath(); c.arc(cx + i * 13 * u + 5 * u, cy, 4.2 * u, 0, TAU);
          if (i < n) { c.fillStyle = T.phos; c.fill(); } else { c.lineWidth = 1.4 * u; c.strokeStyle = T.phos42; c.stroke(); }
        }
        if (n < max) { // refill meter: shield damage taken banks the next grenade
          const bw = max * 13 * u - 3 * u;
          c.beginPath(); c.moveTo(cx, cy + 9 * u); c.lineTo(cx + bw, cy + 9 * u); c.lineWidth = 2 * u; c.strokeStyle = T.phos14; c.stroke();
          c.beginPath(); c.moveTo(cx, cy + 9 * u); c.lineTo(cx + bw * (s.grenadeBank ?? 0), cy + 9 * u); c.strokeStyle = T.phos60; c.stroke();
        }
        return max * 13 * u;
      });
    }
    if (s.otherWeaponName) ky += this.kitRow(x, ky, BIND.swap, s.otherWeaponName, T.phos60, null);
    // the held power-up (and the second slot once the bonus vault opened it); a running buff drains
    const pw = s.powerup;
    if (pw && (pw.name || pw.active)) {
      const label = pw.name || pw.activeName;
      ky += this.kitRow(x, ky, BIND.powerup, label, pw.denied ? T.red : pw.color, (cx, cy) => {
        let w = 0;
        if (pw.active) {
          // the running one: a lamp in its colour and a draining bar
          c.beginPath(); this.diamond(cx + 5 * u, cy, 4.6 * u); c.fillStyle = pw.activeColor; c.fill();
          const bw = 34 * u;
          c.beginPath(); c.moveTo(cx + 14 * u, cy); c.lineTo(cx + 14 * u + bw, cy); c.lineWidth = 3 * u; c.strokeStyle = T.phos14; c.stroke();
          c.beginPath(); c.moveTo(cx + 14 * u, cy); c.lineTo(cx + 14 * u + bw * pw.frac, cy); c.strokeStyle = pw.activeColor; c.stroke();
          w = 14 * u + bw;
        }
        if (pw.name2) { this.txt('+1', cx + w + 6 * u, cy + 3.5 * u, 9 * u, 0, T.phos60, 1, 0.1, false); w += 22 * u; }
        return w;
      });
    }
    // whiskey (heal lane): a pip per bottle in the belt; hurt with one left, the row pulses amber; the heal drains
    const wk = s.whiskey;
    if (wk) {
      const healing = wk.heal > 0 || wk.chug > 0;
      const pulse = wk.low && wk.n > 0 && !healing && Math.floor(t * 2.5) % 2 === 0;
      const col = wk.empty ? T.red : healing || pulse ? T.amber : wk.n > 0 ? T.phos : T.phos42;
      ky += this.kitRow(x, ky, BIND.drink, 'WHISKEY', col, (cx, cy) => {
        const step = 11 * u;
        for (let i = 0; i < wk.max; i++) {
          // a little bottle: body and neck
          const bx = cx + i * step + 4 * u, by = cy;
          c.beginPath();
          c.moveTo(bx - 3 * u, by + 5 * u); c.lineTo(bx - 3 * u, by - 1 * u); c.lineTo(bx - 1.2 * u, by - 3 * u); c.lineTo(bx - 1.2 * u, by - 6 * u);
          c.lineTo(bx + 1.2 * u, by - 6 * u); c.lineTo(bx + 1.2 * u, by - 3 * u); c.lineTo(bx + 3 * u, by - 1 * u); c.lineTo(bx + 3 * u, by + 5 * u); c.closePath();
          if (i < wk.n) { c.fillStyle = healing || pulse ? T.amber : T.phos; c.fill(); } else { c.lineWidth = 1.2 * u; c.strokeStyle = T.phos42; c.stroke(); }
        }
        if (healing) { // the heal running down under the belt
          const bw = wk.max * step - 3 * u, f = wk.chug > 0 ? 1 : wk.heal;
          c.beginPath(); c.moveTo(cx, cy + 9 * u); c.lineTo(cx + bw, cy + 9 * u); c.lineWidth = 2 * u; c.strokeStyle = T.phos14; c.stroke();
          c.beginPath(); c.moveTo(cx, cy + 9 * u); c.lineTo(cx + bw * f, cy + 9 * u); c.strokeStyle = T.amber; c.stroke();
        }
        return wk.max * step;
      });
      // the hand (sim/cards.ts): how many wildcards you're carrying; the draft overlay lists them by name
      if (s.cards && s.cards.length) ky += this.kitRow(x, ky, HAND_BIND, `${s.cards.length} WILDCARD${s.cards.length === 1 ? '' : 'S'}`, T.phos60, null);
    }
    if (s.specialName) {
      const live = s.slotIndex === 2, ammo = s.specialAmmo ?? 0;
      this.kitRow(x, ky, BIND.special, s.specialName, live ? T.amber : ammo > 0 ? T.phos : T.phos42, (cx, cy) => {
        if (ammo > 0) return this.num(ammo, cx, cy + 5 * u, 13 * u, 0, live ? T.amber : T.phos, 1);
        const bw = 40 * u; // recharging from shield damage
        c.beginPath(); c.moveTo(cx, cy); c.lineTo(cx + bw, cy); c.lineWidth = 3 * u; c.strokeStyle = T.phos14; c.stroke();
        c.beginPath(); c.moveTo(cx, cy); c.lineTo(cx + bw * (s.specialBank ?? 0), cy); c.strokeStyle = T.phos60; c.stroke();
        return bw;
      });
    }
    c.globalAlpha = 1;
  }
  /** one line of kit on the disc's left flank: [glyph] NAME [extra]. Returns its pitch. */
  private kitRow(x: number, y: number, b: { kbm: string; pad: string }, name: string, col: string, extra: ((cx: number, cy: number) => number) | null) {
    const u = this.u, size = 9.5 * u, h = 22 * u;
    const nameW = this.tw(name, size, 1, 0.16);
    const extraW = extra ? 56 * u : 0;
    const w = nameW + extraW + 46 * u;
    const fx = x - 128 * u - w;
    this.seat(fx, y, w, h, 5 * u, 1, T.phos26);
    const gw = this.glyph(this.bind(b), fx + 6 * u, y + h / 2, 13 * u, col);
    this.txt(name, fx + 12 * u + gw, y + h / 2 + size * 0.36, size, 0, col, 1, 0.16, false);
    if (extra) extra(fx + 20 * u + gw + nameW, y + h / 2);
    return h + 10 * u;
  }
  private staticOrd() {
    const s = this.s, u = this.u, c = this.c, x = this.ox, y = this.oy;
    c.globalAlpha = s.phase !== 0 ? 0.5 : 1;
    this.disc(x, y, 116 * u, 1, true);
    c.beginPath(); c.arc(x, y, 116 * u - this.hl / 2, 0, TAU); c.lineWidth = this.hl; c.strokeStyle = T.phos14; c.stroke();
    const n = Math.max(1, s.magMax);
    const tickN = n <= 40 ? n : Math.ceil(n / 5);
    this.ringTicks(x, y, 104 * u, tickN, n <= 12 ? 1 : 5, 3 * u, 7 * u, true, T.phos42, this.hl);
    c.beginPath(); this.ringPath(x, y, 103 * u, 0, 1, true); this.ks(this.hl, T.phos26);
    this.engrave('ORDNANCE', x, y - 108 * u);
    c.beginPath(); this.ringPath(x, y, 79 * u - 6 * u - 3 * u, 0, 1, true); c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    const cw = this.num(n, x + 4 * u, y - 23 * u, 14 * u, 0.5, T.phos80, 1);
    c.beginPath(); c.moveTo(x - cw / 2 - 8 * u, y - 23 * u); c.lineTo(x - cw / 2 - 2 * u, y - 34 * u); this.kh(this.hl * 1.6, T.phos80);
    this.txt('RNDS', x, y + 30 * u, 11 * u, 0.5, T.phos80, 1, 0.24);
    this.dataPlate(x, y + 92 * u, s.weaponName || 'SIDEARM', ORD_SN[s.cls & 3]);
    c.globalAlpha = 1;
  }

  // ------------------------------------------------------------------ reticle stack
  private drawReticle(s: HudState) {
    const u = this.u, c = this.c, x = Math.round(this.w / 2), y = Math.round(this.h / 2);
    const hasT = s.aimType >= 0;
    if (s.sight) this.drawSight(s.sight, x, y, hasT);
    else {
      // gun cross: four ticks with a gap, dot 2x2 device px, all keyed
      const g0 = 8 * u, g1 = 21 * u;
      c.beginPath();
      c.moveTo(x - g1, y); c.lineTo(x - g0, y); c.moveTo(x + g0, y); c.lineTo(x + g1, y);
      c.moveTo(x, y + g0); c.lineTo(x, y + g1 * 0.8);
      this.kh(2.4 * u, hasT ? T.phos : T.phos80);
    }
    c.fillStyle = T.keyHard; c.fillRect(x - 1 - this.kp, y - 1 - this.kp, 2 + 2 * this.kp, 2 + 2 * this.kp);
    c.fillStyle = s.aimPrimed ? T.prime : T.phos; c.fillRect(x - 1, y - 1, 2, 2);

    if (hasT) {
      // TARGET DESIGNATOR: four corner brackets that close in on lock (120 ms)
      const p = this.ap[K_TARGET], e = this.ease(Math.min(1, p / 0.55));
      const r = (26 + (1 - e) * 22) * u, k = 8 * u;
      const col = s.aimPrimed ? T.prime : s.aimHyper ? T.hyper : T.phos;
      c.globalAlpha = Math.min(1, p * 3);
      c.beginPath();
      c.moveTo(x - r, y - r + k); c.lineTo(x - r, y - r); c.lineTo(x - r + k, y - r);
      c.moveTo(x + r - k, y - r); c.lineTo(x + r, y - r); c.lineTo(x + r, y - r + k);
      c.moveTo(x + r, y + r - k); c.lineTo(x + r, y + r); c.lineTo(x + r - k, y + r);
      c.moveTo(x - r + k, y + r); c.lineTo(x - r, y + r); c.lineTo(x - r, y + r - k);
      this.ks(1.6 * u, col);
      // data block on a leader: name, code, range, hull/shield bars
      if (e > 0.6 && this.vis(x + r - 4 * this.u, y - 30 * this.u, 250 * this.u, 92 * this.u)) {
        c.globalAlpha = Math.min(1, (e - 0.6) / 0.4);
        const lx = x + r + 14 * u, ly = y + 4 * u;
        c.beginPath(); c.moveTo(x + r, y + r - 10 * u); c.lineTo(lx, ly); c.lineTo(lx + 6 * u, ly); this.ks(this.hl, T.phos60);
        // measured: name + >= 8 px gap + ID; the card grows to fit (cap 230u), then the name shrinks
        const name = ENAME[s.aimType] ?? 'UNKNOWN', code = ECODE[s.aimType] ?? '';
        const cdW = code ? this.tw(code, 10.5 * u, 1, 0.1) : 0, gap = Math.max(8, 10 * u);
        let nsz = 12 * u, nmW = this.tw(name, nsz, 1, 0.08);
        const bw = Math.min(230 * u, Math.max(132 * u, 16 * u + nmW + gap + cdW)), bh = 42 * u, bx = lx + 6 * u, by = ly - 21 * u;
        const room = bw - 16 * u - gap - cdW;
        if (nmW > room) { nsz = Math.max(8 * u, nsz * room / nmW); nmW = this.tw(name, nsz, 1, 0.08); }
        this.chamfer(c, bx, by, bw, bh, 6 * u); c.fillStyle = T.cardSmoke; c.fill();
        this.seat(bx, by, bw, bh, 6 * u, 0.85, s.aimPrimed ? T.prime60 : T.phos26);
        this.txt(name, bx + 8 * u, by + 15 * u, nsz, 0, s.aimPrimed ? T.prime : T.phos, 1, 0.08, true);
        if (code) this.txt(code, bx + bw - 8 * u, by + 14.5 * u, 10.5 * u, 1, T.phos80, 1, 0.1, true);
        const dw = this.num(Math.round(s.aimDist), bx + 8 * u, by + 36 * u, 11.5 * u, 0, T.phos, 1, 0, 1, true);
        this.txt('M', bx + 10 * u + dw, by + 36 * u, 9.5 * u, 0, T.phos80, 1, 0.1, true);
        // hull: 10 segments; shield: hairline over it
        const sx = bx + 42 * u, sw = bw - 50 * u, sy = by + 31 * u;
        c.beginPath();
        for (let i = 0; i < 10; i++) { const a = sx + (sw * i) / 10, b = sx + (sw * (i + 1)) / 10 - 1.5 * u; c.moveTo(a, sy); c.lineTo(b, sy); }
        c.lineWidth = 5 * u; c.strokeStyle = T.phos14; c.stroke();
        const hpN = s.aimHp * 10;
        c.beginPath();
        for (let i = 0; i < 10 && i < hpN; i++) { const a = sx + (sw * i) / 10, b = sx + Math.min((sw * (i + 1)) / 10 - 1.5 * u, (sw * hpN) / 10); c.moveTo(a, sy); c.lineTo(b, sy); }
        c.strokeStyle = s.aimPrimed ? T.prime : T.phos; c.stroke();
        if (s.aimShield > 0) { c.beginPath(); c.moveTo(sx, sy - 6 * u); c.lineTo(sx + sw * s.aimShield, sy - 6 * u); c.lineWidth = 2 * u; c.strokeStyle = T.shield; c.stroke(); }
      }
      c.globalAlpha = 1;
    }
    if (s.aimPrimed) {
      // PRIMED: rotated magenta diamond, breathing
      const pp = this.ap[K_PRIMED], e = this.ease(Math.min(1, pp / 0.6));
      const pulse = 0.5 + 0.5 * Math.sin(s.time * 9);
      const r = (40 + (1 - e) * 20 + pulse * 2.5) * u;
      c.globalAlpha = Math.min(1, pp * 2.5);
      c.beginPath(); this.diamond(x, y, r);
      c.setLineDash([12 * u, 5 * u]); this.ks(2 * u, T.prime); c.setLineDash([]);
      c.beginPath(); this.diamond(x, y - r - 9 * u, 4 * u); c.fillStyle = T.prime; c.fill();
      c.globalAlpha = 1;
    } else if (s.aimHyper) {
      const r = 40 * u;
      c.beginPath();
      for (let i = 0; i < 6; i++) { const a = (i / 6) * TAU + Math.PI / 6; if (i === 0) c.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r); else c.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r); }
      c.closePath(); this.ks(1.6 * u, T.hyper);
    }
    // CHAIN window: ring r52 over the top, burning down from both ends, with the count
    if (s.chainFrac > 0 && this.vis(x - 70 * u, y - 90 * u, 140 * u, 50 * u)) {
      const r = 54 * u, half = 0.95, mid = -Math.PI / 2, hw = half * Math.min(1, s.chainFrac);
      this.arc(x, y, r, mid - half, mid + half, 3 * u, T.prime25, true);
      this.arc(x, y, r, mid - hw, mid + hw, 3 * u, T.prime, false);
      c.beginPath();
      for (let i = -4; i <= 4; i++) { const a = mid + (i / 4) * half; c.moveTo(x + Math.cos(a) * (r + 3 * u), y + Math.sin(a) * (r + 3 * u)); c.lineTo(x + Math.cos(a) * (r + 7 * u), y + Math.sin(a) * (r + 7 * u)); }
      c.lineWidth = this.hl; c.strokeStyle = T.prime60; c.stroke();
      if (s.chainN > 0) {
        const cy = y - r - 14 * u;
        const tw = this.txt('CHAIN', x - 4 * u, cy, 10 * u, 1, T.prime, 1, 0.16);
        void tw;
        c.beginPath(); c.moveTo(x - 1 * u, cy - 7 * u); c.lineTo(x + 5 * u, cy - 1 * u); c.moveTo(x + 5 * u, cy - 7 * u); c.lineTo(x - 1 * u, cy - 1 * u); this.ks(1.4 * u, T.prime);
        this.num(s.chainN, x + 8 * u, cy + 2 * u, 15 * u, 0, T.prime, 1);
      }
      // the loop extender this window gives your class (Hot Shot, Sky-Stalker, Smoke and Mirrors)
      const ext = s.classCue?.extender;
      if (ext) this.txt(ext, x, y - r - (s.chainN > 0 ? 34 : 16) * u, 9.5 * u, 0.5, T.prime, 1, 0.16);
    }
    // reload arc under the reticle
    if (s.reloadFrac >= 0) {
      const r = 34 * u, a0 = Math.PI / 2 + 0.6;
      this.arc(x, y, r, Math.PI / 2 - 0.6, a0, 2.5 * u, T.phos14, true);
      c.beginPath(); c.arc(x, y, r, a0, a0 - 1.2 * s.reloadFrac, true); this.ks(2.5 * u, T.shield, false);
    }
    // heat ticker to the left once the pack is hot (the DoT warning where the eyes are)
    const htF = s.heat / Math.max(1, s.heatMax);
    if (htF >= 0.6) {
      const r = 42 * u, a1 = Math.PI + 0.8;
      this.arc(x, y, r, Math.PI - 0.8, a1, 2 * u, T.phos14, true);
      c.beginPath(); c.arc(x, y, r, a1, a1 - 1.6 * Math.min(1, htF), true); this.ks(2 * u, htF >= 0.8 ? T.red : T.amber, false);
    }
    // confirms (shooting feel): the marker says what the hit DID. Body: four ticks, sized by how
    // hard it landed. Crit: bigger amber ticks that pop in and settle, a dot of light. Armour: short
    // dim ticks pushed out and a flat bar (it glanced off, go AP or go round). Shield: a broken
    // hex in shield blue. Kill: a red X with a ring that runs out; headshot kills go amber with a
    // diamond, heavy robots throw a second ring; a multikill counts up under the reticle.
    const hk = s.hitKind ?? 0, hp = Math.min(1.5, s.hitPow ?? 0.5);
    if (hk === 1 ? s.headAge < 0.3 : s.hitAge < 0.2) {
      const age = hk === 1 ? s.headAge : s.hitAge;
      const k = age / (hk === 1 ? 0.3 : 0.2);
      if (hk === 1) {
        const pop = 1 + 0.45 * Math.max(0, 1 - age / 0.06); // 60 ms overshoot then settle
        c.globalAlpha = 1 - k * k;
        this.cross(x, y, (10 + k * 3) * u * pop, (25 + k * 4) * u * pop, 3 * u, T.amber);
        c.fillStyle = T.amber; c.globalAlpha = (1 - k) * 0.9;
        c.beginPath(); this.diamond(x, y, 3.5 * u * pop); c.fill();
      } else if (hk === 2) {
        c.globalAlpha = (1 - k) * 0.85;
        this.cross(x, y, (14 + k * 6) * u, (19 + k * 6) * u, 1.6 * u, T.phos60);
        c.beginPath(); c.moveTo(x - 7 * u, y + 13 * u); c.lineTo(x + 7 * u, y + 13 * u); this.ks(1.6 * u, T.phos60);
      } else if (hk === 3) {
        c.globalAlpha = (1 - k) * 0.95;
        const r = (15 + k * 5) * u;
        c.beginPath();
        for (let i = 0; i < 6; i++) {
          const a0 = (i / 6) * TAU + Math.PI / 6 + 0.12, a1 = ((i + 1) / 6) * TAU + Math.PI / 6 - 0.12;
          c.moveTo(x + Math.cos(a0) * r, y + Math.sin(a0) * r); c.lineTo(x + Math.cos(a1) * r, y + Math.sin(a1) * r);
        }
        this.ks(2 * u, T.shield);
      } else {
        const sz = 0.8 + 0.35 * hp;
        c.globalAlpha = 1 - k * 0.7;
        this.cross(x, y, (8 + k * 3) * u * sz, (15 + k * 3) * u * sz, (1.6 + 0.8 * hp) * u, T.phos);
      }
      c.globalAlpha = 1;
    }
    if (s.killAge < 0.5) {
      const k = s.killAge / 0.5, head = !!s.killHead, big = !!s.killBig;
      const pop = 1 + 0.35 * Math.max(0, 1 - s.killAge / 0.07);
      const col = head ? T.amber : T.red;
      c.globalAlpha = 1 - k * k;
      this.cross(x, y, 11 * u * pop, (29 + k * 8) * u * pop, (big ? 4 : 3.2) * u, col);
      this.arc(x, y, (22 + k * 26) * u, 0, TAU, this.hl * 1.6, T.phos, false);
      if (big) this.arc(x, y, (16 + k * 44) * u, 0, TAU, this.hl * 2.4, col, false);
      if (head) { c.beginPath(); this.diamond(x, y, (8 + k * 14) * u); this.ks(1.8 * u, T.amber); }
      c.globalAlpha = 1;
    }
    const kn = s.killN ?? 0;
    if (kn >= 2 && s.killAge < 1.4) {
      const k = Math.max(0, (s.killAge - 0.9) / 0.5);
      const pop = 1 + 0.3 * Math.max(0, 1 - s.killAge / 0.08);
      c.globalAlpha = 1 - k;
      const tw = this.txt('×', x - 2 * u, y + 58 * u, 13 * u * pop, 1, T.red, 1, 0);
      void tw;
      this.num(kn, x, y + 60 * u, 19 * u * pop, 0, T.red, 1);
      c.globalAlpha = 1;
    }
    if (s.detonateAge < 0.55) {
      const k = s.detonateAge / 0.55, r = (30 + k * 70) * u;
      c.globalAlpha = 1 - k; c.beginPath(); this.diamond(x, y, r); this.ks((4 - k * 2.5) * u, T.prime); c.globalAlpha = 1;
    }
  }
  /** Each gun's own sight (GUN_IDENTITIES.md): you can tell what's in your hands from the middle of
   *  the screen. Rings are true to the spread cone, so the shotgun's circle is where the pellets go. */
  private drawSight(g: HudSight, x: number, y: number, hasT: boolean) {
    const u = this.u, c = this.c, H2 = this.h / 2;
    const ring = (f: number, min: number) => Math.min(235 * u, Math.max(min * u, f * H2));
    const ink = hasT ? T.phos : T.phos80;
    const lockMark = (two: boolean, col: string) => {
      if (!g.lock) return;
      const lx = g.lockX, ly = g.lockY, k = 9 * u;
      if (Math.abs(lx - x) > 300 * u || Math.abs(ly - y) > 240 * u) return; // stay inside the layer
      c.beginPath();
      if (two) { this.diamond(lx - 12 * u, ly, 5 * u); this.diamond(lx + 12 * u, ly, 5 * u); }
      else this.diamond(lx, ly, k);
      this.ks(1.8 * u, col);
      // the tether: where the round will bend
      c.beginPath(); c.moveTo(x + (lx - x) * 0.18, y + (ly - y) * 0.18); c.lineTo(x + (lx - x) * 0.72, y + (ly - y) * 0.72);
      c.setLineDash([3 * u, 4 * u]); this.ks(this.hl, T.phos42, false); c.setLineDash([]);
    };
    const coneTicks = (r: number, n: number) => {
      c.beginPath();
      for (let i = 0; i < n; i++) {
        const a = (i / n) * TAU + Math.PI / 4;
        c.moveTo(x + Math.cos(a - 0.07) * r, y + Math.sin(a - 0.07) * r); c.arc(x, y, r, a - 0.07, a + 0.07);
      }
      this.ks(1.2 * u, T.phos42, false);
    };
    switch (g.gun) {
      case 0: { // The Starter: a small cross inside the tracking cone, a diamond on whatever it'll find
        this.cross(x, y, 5 * u, 13 * u, 2 * u, ink);
        coneTicks(ring(g.track, 40), 4);
        lockMark(false, T.phos);
        break;
      }
      case 7: { // Akimbo: two chevrons for two guns, a wide cone, a pip either side of the lock
        c.beginPath();
        c.moveTo(x - 16 * u, y - 6 * u); c.lineTo(x - 10 * u, y); c.lineTo(x - 16 * u, y + 6 * u);
        c.moveTo(x + 16 * u, y - 6 * u); c.lineTo(x + 10 * u, y); c.lineTo(x + 16 * u, y + 6 * u);
        this.kh(2 * u, ink);
        coneTicks(ring(g.track, 50), 8);
        lockMark(true, T.phos);
        break;
      }
      case 1: { // Pulse Rifle: three segments, one per round of the burst; land all three to overload
        const r = 15 * u;
        for (let i = 0; i < 3; i++) {
          const a0 = -Math.PI / 2 + (i / 3) * TAU + 0.22, a1 = a0 + TAU / 3 - 0.44;
          const lit = i < g.burstHits, missed = !lit && i < g.burstRound;
          this.arc(x, y, r, a0, a1, 2.4 * u, lit ? (g.burstHits >= 3 ? T.amber : T.phos) : missed ? T.red60 : T.phos42);
        }
        if (g.overloadAge < 0.4) {
          const k = g.overloadAge / 0.4;
          c.globalAlpha = 1 - k;
          this.arc(x, y, (19 + k * 22) * u, 0, TAU, 2.6 * u * (1 - k * 0.6), T.amber, false);
          this.txt('OVERLOAD', x, y + 40 * u, 10 * u, 0.5, T.amber, 1, 0.18, true);
          c.globalAlpha = 1;
        }
        break;
      }
      case 2: { // Auto MG: the bloom ring grows with the spin; the RPM arc under it says how hot the barrels are
        const r = ring(g.spread, 9);
        this.arc(x, y, r, 0, TAU, 1.4 * u, T.phos60);
        c.beginPath();
        for (let i = 0; i < 4; i++) { const a = (i / 4) * TAU; c.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r); c.lineTo(x + Math.cos(a) * (r + 5 * u), y + Math.sin(a) * (r + 5 * u)); }
        this.ks(1.6 * u, ink);
        const rr = r + 10 * u, a0 = Math.PI / 2 + 0.7, sw = 1.4;
        this.arc(x, y, rr, a0 - sw, a0, 3 * u, T.phos14);
        if (g.spin > 0.01) { c.beginPath(); c.arc(x, y, rr, a0, a0 - sw * g.spin, true); this.ks(3 * u, g.spin >= 0.99 ? T.amber : T.phos, false); }
        break;
      }
      case 3: { // Bolt-Action: a fine long cross, chevrons close in as you hold aim; amber = dead-eye
        c.beginPath();
        c.moveTo(x - 34 * u, y); c.lineTo(x - 4 * u, y); c.moveTo(x + 4 * u, y); c.lineTo(x + 34 * u, y);
        c.moveTo(x, y + 4 * u); c.lineTo(x, y + 30 * u); c.moveTo(x, y - 4 * u); c.lineTo(x, y - 18 * u);
        this.kh(1.2 * u, ink);
        if (g.ads) {
          const full = g.steady >= 0.99, d = (42 - 32 * g.steady) * u, k = 5 * u;
          c.beginPath();
          for (let i = 0; i < 4; i++) {
            const sx = i & 1 ? 1 : -1, sy = i & 2 ? 1 : -1;
            const cx = x + sx * d * Math.SQRT1_2, cy = y + sy * d * Math.SQRT1_2;
            c.moveTo(cx + sx * k, cy); c.lineTo(cx, cy); c.lineTo(cx, cy + sy * k);
          }
          this.ks(1.8 * u, full ? T.amber : T.phos80);
          if (g.steady > 0.05) this.txt(`+${Math.round(g.steady * 60)}%`, x + 40 * u, y + 34 * u, 9.5 * u, 0, full ? T.amber : T.phos60, 1, 0.1, true);
        }
        break;
      }
      case 4: { // The Gentlemen: the holo ring IS the spread; aim to shrink it. Pellet ticks ride the ring.
        const r = ring(g.spread, 12);
        this.arc(x, y, r, 0, TAU, 1.6 * u, g.ads ? T.phos80 : T.phos60);
        c.beginPath();
        for (let i = 0; i < 12; i++) { const a = (i / 12) * TAU; c.moveTo(x + Math.cos(a) * (r - 2.5 * u), y + Math.sin(a) * (r - 2.5 * u)); c.lineTo(x + Math.cos(a) * (r + 2.5 * u), y + Math.sin(a) * (r + 2.5 * u)); }
        this.ks(1.4 * u, ink);
        // inner glow ring: a soft fill so it reads as a projected hologram, not a line
        c.beginPath(); c.arc(x, y, r, 0, TAU); c.fillStyle = T.phos14; c.globalAlpha = 0.35; c.fill(); c.globalAlpha = 1;
        break;
      }
      case 5: { // Big Iron: a front-sight post, six chamber pips; a scatter ring blooms when you fan from the hip
        c.beginPath(); c.moveTo(x, y + 4 * u); c.lineTo(x, y + 20 * u); c.moveTo(x - 12 * u, y + 20 * u); c.lineTo(x + 12 * u, y + 20 * u);
        this.kh(2.6 * u, ink);
        const n = Math.max(1, g.chamberMax);
        const br = !g.ads && g.bloom > 0.05 ? ring(g.spread, 8) : 0, pr = Math.max(34 * u, br + 9 * u);
        for (let i = 0; i < n; i++) {
          const a = Math.PI / 2 + ((i - (n - 1) / 2) / n) * 1.6;
          const px = x + Math.cos(a) * pr, py = y + Math.sin(a) * pr;
          c.beginPath(); c.arc(px, py, 2.6 * u, 0, TAU);
          if (i < g.chambers) { c.fillStyle = T.phos; c.fill(); } else this.ks(1 * u, T.phos42);
        }
        if (br > 0) this.arc(x, y, br, 0, TAU, 1.4 * u, g.bloom > 1.5 ? T.amber60 : T.phos42);
        break;
      }
      case 6: { // Duel Blaster: a charge ring; full = it locks on (amber brackets on the robot)
        const r = 17 * u;
        this.arc(x, y, r, 0, TAU, 2 * u, T.phos26);
        if (g.charge >= 0) {
          const full = g.charge >= 1;
          c.beginPath(); c.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + TAU * g.charge); this.ks(2.6 * u, full ? T.amber : T.shield, false);
          if (full) lockMark(false, T.amber);
          else if (g.lock) { c.globalAlpha = 0.45; lockMark(false, T.phos60); c.globalAlpha = 1; }
        }
        c.beginPath(); c.moveTo(x, y - r - 4 * u); c.lineTo(x, y - r + 4 * u); this.ks(1.6 * u, ink); // 12 o'clock index
        break;
      }
      case 8: { // The Scoundrel: one half-ring per barrel; aim and they close up, both barrels together
        const r = ring(g.spread, 10);
        const col = g.ads ? T.amber : T.phos80;
        this.arc(x, y, r, Math.PI / 2 + 0.35, Math.PI * 1.5 - 0.35, 2 * u, col);
        this.arc(x, y, r, -Math.PI / 2 + 0.35, Math.PI / 2 - 0.35, 2 * u, col);
        for (let i = 0; i < 2; i++) {
          c.beginPath(); c.arc(x - 5 * u + i * 10 * u, y + r + 10 * u, 2.6 * u, 0, TAU);
          if (i < g.chambers) { c.fillStyle = T.phos; c.fill(); } else this.ks(1 * u, T.phos42);
        }
        break;
      }
      default: this.cross(x, y, 8 * u, 21 * u, 2.4 * u, ink);
    }
  }

  private cross(x: number, y: number, r0: number, r1: number, lw: number, col: string) {
    const c = this.c, d = Math.SQRT1_2;
    c.beginPath();
    for (let i = 0; i < 4; i++) {
      const sx = i & 1 ? 1 : -1, sy = i & 2 ? 1 : -1;
      c.moveTo(x + sx * r0 * d, y + sy * r0 * d); c.lineTo(x + sx * r1 * d, y + sy * r1 * d);
    }
    this.ks(lw, col);
  }

  private drawFlight(s: HudState) {
    const u = this.u, c = this.c, x = Math.round(this.w / 2), y = Math.round(this.h / 2 + 30 * u);
    // flight path marker: circle, wings, fin (HMD lineage)
    c.beginPath(); c.arc(x, y, 7 * u, 0, TAU);
    c.moveTo(x - 7 * u, y); c.lineTo(x - 21 * u, y); c.moveTo(x + 7 * u, y); c.lineTo(x + 21 * u, y);
    c.moveTo(x, y - 7 * u); c.lineTo(x, y - 14 * u);
    this.ks(1.8 * u, T.phos);
    // pitch-ladder brackets and a heat caret on the right ladder
    c.beginPath();
    for (let i = -1; i <= 1; i += 2) {
      c.moveTo(x - 70 * u, y + i * 44 * u); c.lineTo(x - 40 * u, y + i * 44 * u); c.lineTo(x - 40 * u, y + i * 37 * u);
      c.moveTo(x + 70 * u, y + i * 44 * u); c.lineTo(x + 40 * u, y + i * 44 * u); c.lineTo(x + 40 * u, y + i * 37 * u);
    }
    this.ks(this.hl, T.phos42);
    const htF = Math.min(1, s.heat / Math.max(1, s.heatMax));
    const hx = x + 84 * u, top = y - 44 * u, bot = y + 44 * u;
    c.beginPath(); c.moveTo(hx, top); c.lineTo(hx, bot); this.ks(3 * u, T.phos14);
    c.beginPath(); c.moveTo(hx, bot); c.lineTo(hx, bot - (bot - top) * htF); this.ks(3 * u, htF >= 0.8 ? T.red : T.amber, false);
    const ry = bot - (bot - top) * 0.8;
    c.beginPath(); c.moveTo(hx - 5 * u, ry); c.lineTo(hx + 5 * u, ry); this.ks(this.hl * 1.5, T.red);
    this.txt('HEAT', hx, top - 6 * u, 7.5 * u, 0.5, T.phos60, 0, 0.25);
  }

  // threat compass: wedges on r150 toward the attacker, ≤ 1 s
  private drawHurt(s: HudState) {
    const u = this.u, x = this.w / 2, y = this.h / 2, c = this.c;
    for (let i = 0; i < s.hurtDirs.length; i++) {
      const d = s.hurtDirs[i];
      if (d.age >= 1) continue;
      const a = d.angle - Math.PI / 2;
      const k = d.age < 0.016 ? 1 : 0.35 + 0.65 * Math.exp(-d.age / 0.32);
      c.globalAlpha = Math.min(1, k * (1 - d.age));
      c.beginPath(); c.arc(x, y, 150 * u, a - 0.24, a + 0.24); this.kh(7 * u, T.red);
      c.beginPath();
      const ca = Math.cos(a), sa = Math.sin(a), px = -sa, py = ca, R = 162 * u;
      c.moveTo(x + ca * (R + 8 * u), y + sa * (R + 8 * u)); c.lineTo(x + ca * R + px * 7 * u, y + sa * R + py * 7 * u); c.lineTo(x + ca * R - px * 7 * u, y + sa * R - py * 7 * u); c.closePath();
      c.lineWidth = this.kp * 3.4; c.strokeStyle = T.keyHard; c.stroke(); c.fillStyle = T.red; c.fill();
    }
    c.globalAlpha = 1;
  }

  private drawCoaching(s: HudState) {
    if (s.move === 3 || s.aimType < 0) return;
    const u = this.u, c = this.c, x = this.w / 2;
    let y = this.h / 2 + 72 * u;
    const p = this.ap[K_COACH];
    if (s.aimHyper) { this.coachTab(s.detonatorVerb, ' BREAKS HYPER SHIELD', x, y, T.hyper, p); y += 30 * u; }
    if (s.aimPrimed) {
      // the magenta plate: what detonates it
      if (this.vis(x - 220 * u, y - 16 * u, 440 * u, 32 * u)) {
      const size = 13 * u;
      const cue = s.classCue;
      const str = cue && cue.detReady && cue.detVerb ? cue.detVerb : s.detonatorVerb;
      const tw = this.tw('PRIMED', size * 0.75, 1, 0.2) + 14 * u + this.tw(str, size, 1, 0.12);
      const ph = size * 2.0, pw = tw + size * 3.2, k = ph * 0.32;
      const e = this.ease(Math.min(1, this.ap[K_PRIMED] / 0.7)), w = pw * e;
      this.chamfer(c, x - w / 2, y - ph / 2, w, ph, k);
      c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke();
      c.fillStyle = T.primePlate; c.fill();
      if (e > 0.85) {
        let cx = x - tw / 2 + 6 * u;
        c.beginPath(); this.diamond(cx - 10 * u, y, 4 * u); c.fillStyle = T.plateText; c.fill();
        cx += this.txt('PRIMED', cx, y + size * 0.27, size * 0.75, 0, 'rgba(255,230,244,0.85)', 1, 0.2, false) + 14 * u;
        this.txt(str, cx, y + size * 0.36, size, 0, T.plateText, 1, 0.12, false);
      }
      }
      let y2 = y + 30 * u;
      if (s.aimShield > 0) { this.coachTab('SHIELD UP', ' · DROP IT FIRST', x, y2, T.shield, p); y2 += 30 * u; }
      // not lined up yet: what to do so the detonator lands (get behind it, get airborne, wait for shield)
      if (s.classCue && !s.classCue.detReady && s.classCue.detHint) this.coachTab(s.classCue.detHint, '', x, y2, T.amber, p);
    } else if (s.aimType !== 0) {
      // the button that primes: the ability for a dash primer (Valkyrie), melee for everyone else
      const g = this.bind(s.primerVerb === s.abilityName && s.abilityName ? BIND.ability : BIND.melee);
      const cue = s.classCue;
      if (cue && !cue.primeReady && cue.primeHint) this.coachTab(s.primerVerb, ` · ${cue.primeHint}`, x, y, T.amber, p, g);
      else this.coachTab(s.primerVerb, ' TO PRIME', x, y, T.phos, p, g);
    }
  }
  /** one caption on a glass tab with chevron rails: an emphasised verb and a quieter rest */
  private coachTab(verb: string, rest: string, x: number, y: number, col: string, p: number, glyph = '') {
    const u = this.u, c = this.c, size = 11 * u;
    if (!this.vis(x - 260 * u, y - 14 * u, 520 * u, 28 * u)) return;
    const gw = glyph ? this.glyphW(glyph, 13 * u) + 7 * u : 0;
    const w1 = this.tw(verb, size, 1, 0.12), w2 = this.tw(rest, size, 0, 0.12);
    const tw = gw + w1 + w2, h = 22 * u;
    const e = this.ease(Math.min(1, p / 0.8)), w = (tw + 26 * u) * e;
    this.seat(x - w / 2, y - h / 2, w, h, 5 * u, 1, T.phos26);
    c.beginPath();
    const L = x - w / 2, R = x + w / 2;
    c.moveTo(L - 20 * u, y); c.lineTo(L - 8 * u, y); c.moveTo(L - 12 * u, y - 4 * u); c.lineTo(L - 8 * u, y); c.lineTo(L - 12 * u, y + 4 * u);
    c.moveTo(R + 20 * u, y); c.lineTo(R + 8 * u, y); c.moveTo(R + 12 * u, y - 4 * u); c.lineTo(R + 8 * u, y); c.lineTo(R + 12 * u, y + 4 * u);
    this.ks(this.hl * 1.4, col);
    if (e > 0.85) {
      let x0 = x - tw / 2;
      if (glyph) { this.glyph(glyph, x0, y, 13 * u); x0 += gw; }
      this.txt(verb, x0, y + size * 0.36, size, 0, col, 1, 0.12, false);
      this.txt(rest, x0 + w1 + 0.12 * size, y + size * 0.36, size, 0, T.phos80, 0, 0.12, false);
    }
  }
  /** how wide glyph() will draw, without drawing */
  private glyphW(id: string, size: number) {
    if (this.s.glyphs === 'pad' && (id === '✕' || id === '○' || id === '□' || id === '△')) return size * 1.2;
    if (id === 'LMB' || id === 'RMB') return size * 0.8;
    return Math.max(size * 1.2, this.tw(id, size * 0.66, 1, 0.05) + size * 0.6);
  }

  // ------------------------------------------------------------------ CALLS: callouts, chat wheel, radio
  /** the pickup card: a glass plate naming the drop, what it does, and the one thing to do about it */
  private drawPickup(pk: HudPickup) {
    const u = this.u, c = this.c, g = this.g, W = this.w, H = this.h;
    if (!this.vis(W - 750 * u, H - 480 * u, 440 * u, 190 * u)) return;
    // sized to its copy: short power-up lines don't float in an empty box
    const pad = 16 * u;
    const rowW = (pk.glyph ? this.glyphW(pk.glyph, 16 * u) + 8 * u : 0) + this.tw(pk.verb, 12.5 * u, 1, 0.14) + this.tw(pk.rest, 11 * u, 0, 0.08) + 2 * u;
    const want = Math.max(this.tw(pk.tag, 10 * u, 1, 0.16), this.tw(pk.title, 21 * u, 1, 0.08) + 18 * u, rowW, Math.min(this.tw(pk.blurb, 13.5 * u, 1, 0.03), 330 * u));
    const w = Math.max(260 * u, Math.min(400 * u, want + pad * 2 + 6 * u));
    const inner = w - pad * 2 - 6 * u;
    // the blurb, wrapped to two lines at most (never one word alone on the second)
    const bs = 13.5 * u;
    let b1 = pk.blurb, b2 = '', b3 = '';
    if (this.tw(b1, bs, 1, 0.03) > inner) {
      // a number stays with its unit ("15 second")
      const words = pk.blurb.replace(/(\d) /g, '$1\u00a0').split(' ');
      let k = words.length - 1;
      while (k > 1 && this.tw(words.slice(0, k).join(' '), bs, 1, 0.03) > inner) k--;
      if (k === words.length - 1 && k > 2) k--;
      b1 = words.slice(0, k).join(' ');
      // the rest: a second line, and a third when it still doesn't fit (never cut short)
      const rest = words.slice(k);
      let j = rest.length;
      while (j > 1 && this.tw(rest.slice(0, j).join(' '), bs, 1, 0.03) > inner) j--;
      b2 = rest.slice(0, j).join(' '); b3 = rest.slice(j).join(' ');
    }
    const h = (b3 ? 158 : b2 ? 140 : 120) * u, x0 = W - 330 * u - w, y0 = H - 300 * u - h;
    // open: the plate unrolls from the centre, then the ink comes up
    const e = this.ease(Math.min(1, pk.age / 0.22)), ink = Math.min(1, Math.max(0, (pk.age - 0.1) / 0.18));
    const a = pk.fade;
    if (a <= 0.01) return;
    c.globalAlpha = a; g.globalAlpha = a;
    const ww = w * e;
    this.seat(x0, y0, ww, h, 9 * u, 0.66, T.phos26);
    g.globalAlpha = 1;
    // the accent rail down the left edge, in the item's colour
    c.fillStyle = pk.color; c.globalAlpha = a * 0.85;
    c.fillRect(x0 + 7 * u, y0 + 12 * u, 2.5 * u, h - 24 * u);
    if (ink <= 0) { c.globalAlpha = 1; return; }
    c.globalAlpha = a * ink;
    const lx = x0 + pad + 6 * u;
    let y = y0 + 23 * u;
    // tag line (the world marker over the item owns the distance)
    this.txt(pk.tag, lx, y, 10 * u, 0, T.amber, 1, 0.16, false);
    // the name, with a lit diamond in its colour
    y += 26 * u;
    c.beginPath(); this.diamond(lx + 5 * u, y - 7 * u, 5 * u); c.fillStyle = pk.color; c.fill();
    this.txt(pk.title, lx + 18 * u, y, 21 * u, 0, T.phos, 1, 0.08, false);
    // what it does
    y += 21 * u;
    this.txt(b1, lx, y, bs, 0, T.phos, 1, 0.03, false);
    if (b2) { y += 18 * u; this.txt(b2, lx, y, bs, 0, T.phos, 1, 0.03, false); }
    if (b3) { y += 18 * u; this.txt(b3, lx, y, bs, 0, T.phos, 1, 0.03, false); }
    // a hairline, then what to do about it
    y += 12 * u;
    c.globalAlpha = a * ink * 0.6;
    c.fillStyle = T.phos26; c.fillRect(lx, y, x0 + w - 8 * u - lx, this.hl);
    c.globalAlpha = a * ink;
    y += 19 * u;
    let cx = lx;
    const vcol = T.amber;
    if (pk.glyph) { cx += this.glyph(pk.glyph, cx, y - 4.5 * u, 16 * u) + 8 * u; }
    cx += this.txt(pk.verb, cx, y, 12.5 * u, 0, vcol, 1, 0.14, false);
    if (pk.rest) this.txt(pk.rest, cx + 2 * u, y, 11 * u, 0, T.phos80, 0, 0.08, false);
    // ACQUIRED: one scan line sweeps the plate as it lands
    if (pk.mode === 'got' && pk.age < 0.6) {
      const k = pk.age / 0.6, sy = y0 + h * k;
      c.globalAlpha = a * (1 - k) * 0.8;
      c.fillStyle = pk.color; c.fillRect(x0 + 10 * u, sy, w - 20 * u, 1.5 * u);
    }
    c.globalAlpha = 1;
  }

  private drawCalls(s: HudState) {
    const u = this.u, c = this.c, W = this.w, H = this.h;
    // callouts: newest on top, older ones step down and shrink
    const cs = s.callouts ?? [];
    let y = 236 * u;
    for (let i = cs.length - 1; i >= 0; i--) {
      const k = cs[i];
      const a = this.callAlpha(k.age);
      if (a <= 0) continue;
      const newest = i === cs.length - 1;
      const pop = newest ? 1 + 0.18 * Math.max(0, 1 - k.age / 0.2) : 0.72;
      const size = 26 * u * pop;
      const col = k.tone === 'prime' ? T.prime : k.tone === 'amber' ? T.amber : k.tone === 'red' ? T.red : T.phos;
      c.globalAlpha = a; this.g.globalAlpha = a;
      if (newest && this.vis(W / 2 - 380 * u, y - 40 * u, 760 * u, 90 * u)) {
        const tw = this.tw(k.text, size, 1, 0.14);
        // keep the plate inside the layer's rect: a long sub is cut short
        let sub = k.sub;
        while (sub.length > 4 && this.tw(sub, 10 * u, 0, 0.2) > 660 * u) sub = sub.slice(0, -2).trimEnd();
        if (sub !== k.sub) sub += '…';
        const sw = sub ? this.tw(sub, 10 * u, 0, 0.2) : 0;
        const pw = Math.max(tw, sw) + 70 * u, ph = (k.sub ? 58 : 42) * u;
        this.seat(W / 2 - pw / 2, y - size * 0.95, pw, ph, 10 * u, 0.9, T.phos26);
        // the tone rails either side
        c.fillStyle = col; c.fillRect(W / 2 - pw / 2 + 8 * u, y - size * 0.6, 3 * u, size * 0.75); c.fillRect(W / 2 + pw / 2 - 11 * u, y - size * 0.6, 3 * u, size * 0.75);
        this.txt(k.text, W / 2, y, size, 0.5, col, 1, 0.14);
        if (sub) this.txt(sub, W / 2, y + 20 * u, 10 * u, 0.5, T.phos80, 0, 0.2);
        y += ph + 4 * u;
      } else if (!newest && this.vis(W / 2 - 300 * u, y - 24 * u, 600 * u, 34 * u)) {
        this.txt(k.text, W / 2, y, size * 0.7, 0.5, col, 1, 0.14);
        y += 24 * u;
      }
    }
    c.globalAlpha = 1; this.g.globalAlpha = 1;

    // quick-chat wheel: four lines around the reticle, the one the stick points at lit
    const wh = s.wheel;
    if (wh?.open && this.vis(W / 2 - 230 * u, H / 2 - 190 * u, 460 * u, 380 * u)) {
      const cx = W / 2, cy = H / 2, R = 118 * u;
      this.disc(cx, cy, 34 * u, 0.7, false);
      this.txt(wh.sel < 0 ? 'PING' : 'SAY', cx, cy + 4 * u, 10 * u, 0.5, T.phos60, 1, 0.2);
      for (let i = 0; i < 4; i++) {
        const a = -Math.PI / 2 + i * Math.PI / 2, lx = cx + Math.cos(a) * R, ly = cy + Math.sin(a) * R * 0.78;
        const on = wh.sel === i, str = wh.lines[i] ?? '';
        const size = 13 * u, tw = this.tw(str, size, 1, 0.12) + 28 * u, th = 28 * u;
        this.seat(lx - tw / 2, ly - th / 2, tw, th, 6 * u, on ? 1 : 0.7, on ? T.amber : T.phos26);
        this.txt(str, lx, ly + size * 0.36, size, 0.5, on ? T.amber : T.phos80, 1, 0.12, false);
      }
      const g = this.bind(BIND.ping);
      const hint = 'POINT THE STICK · LET GO TO SAY IT';
      const hw = this.tw(hint, 9 * u, 0, 0.2);
      this.glyph(g, cx - hw / 2 - this.glyphW(g, 12 * u) - 6 * u, cy + R + 4 * u, 12 * u);
      this.txt(hint, cx - hw / 2, cy + R + 4 * u + 3 * u, 9 * u, 0, T.phos60, 0, 0.2);
    }

    // Pop on the radio: a caption above the caution strip
    const co = s.coach;
    if (co && this.vis(W / 2 - 460 * u, H - 172 * u, 920 * u, 72 * u)) {
      const a = this.coachAlpha(co.age);
      if (a > 0) {
        c.globalAlpha = a; this.g.globalAlpha = a;
        const size = 13 * u, y0 = H - 136 * u;
        const lab = 'POP · RADIO';
        const lw = this.tw(lab, 8.5 * u, 1, 0.24) + 14 * u;
        const g = co.glyph ? this.bind((BIND as Record<string, { kbm: string; pad: string }>)[co.glyph] ?? { kbm: '', pad: '' }) : '';
        const gw = g ? this.glyphW(g, 14 * u) + 8 * u : 0;
        // stay clear of the kit rows on the right (they reach in to about W - 470u at 16:9): a line
        // too long for the gap wraps onto a second row instead of running under the ordnance
        const maxW = Math.max(420 * u, Math.min(900 * u, 2 * (W / 2 - 470 * u)));
        const room = maxW - lw - gw - 36 * u;
        let l1 = co.text, l2 = '';
        if (this.tw(l1, size, 0, 0.06) > room) {
          const words = co.text.split(' ');
          let k = words.length - 1;
          while (k > 1 && this.tw(words.slice(0, k).join(' '), size, 0, 0.06) > room) k--;
          l1 = words.slice(0, k).join(' '); l2 = words.slice(k).join(' ');
        }
        const tw = Math.max(this.tw(l1, size, 0, 0.06), l2 ? this.tw(l2, size, 0, 0.06) : 0);
        const w = Math.min(maxW, lw + gw + tw + 36 * u), h = (l2 ? 50 : 32) * u, x0 = W / 2 - w / 2;
        const yc = l2 ? y0 - 9 * u : y0; // the plate grows upward, staying above the caution strip
        this.seat(x0, yc - h / 2, w, h, 7 * u, 0.9, T.phos26);
        c.fillStyle = T.amber; c.fillRect(x0 + 6 * u, yc - h / 2 + 7 * u, 2 * u, h - 14 * u);
        let cx = x0 + 16 * u;
        const ly = l2 ? yc - 9 * u : yc;
        cx += this.txt(lab, cx, ly + 3 * u, 8.5 * u, 0, T.amber, 1, 0.24, false) + 14 * u;
        if (g) { this.glyph(g, cx, ly, 14 * u); cx += gw; }
        this.txt(l1, cx, ly + size * 0.36, size, 0, T.phos, 0, 0.06, false);
        if (l2) this.txt(l2, cx, ly + 18 * u + size * 0.36, size, 0, T.phos, 0, 0.06, false);
        c.globalAlpha = 1; this.g.globalAlpha = 1;
      }
    }
  }

  /** MASTER CAUTION strip: solid red plates in a row at bottom centre, between the two rings and
   *  under the cowboy's boots, so they read as shapes in the periphery without covering him */
  private drawAlarms(s: HudState) {
    const u = this.u, c = this.c, size = 14 * u, gap = 12 * u;
    const blink = Math.floor(s.time * 4.4) % 5 < 3;
    const y = this.h - 64 * u;
    const heat = this.ap[K_DENIED] > 0 ? 1 : this.ap[K_OVER] > 0 ? 2 : 0;
    const sh = this.ap[K_SHIELD] > 0, fire = this.ap[K_FIRE] > 0;
    const wHeat = heat ? this.alarmW(heat === 1 ? 'HEAT DENIED' : 'OVERHEAT', size) : 0;
    const wSh = sh ? this.alarmW('SHIELD OFFLINE', size) : 0;
    const wFire = fire ? this.alarmW('ON FIRE', size) : 0;
    const n = (heat ? 1 : 0) + (sh ? 1 : 0) + (fire ? 1 : 0);
    if (!n) return;
    let x = this.w / 2 - (wHeat + wSh + wFire + gap * (n - 1)) / 2;
    if (sh) { this.alarm('SHIELD OFFLINE', x + wSh / 2, y, size, this.ap[K_SHIELD], T.plateRed, T.plateRedEdge, s.shieldBrokeAge < 0.2 ? (1 - s.shieldBrokeAge / 0.2) * 0.6 : 0); x += wSh + gap; }
    if (heat === 1) { this.alarm('HEAT DENIED', x + wHeat / 2, y, size, this.ap[K_DENIED], T.plateRed, T.plateRedEdge, Math.max(0, 1 - s.heatDeniedAge / 0.2) * 0.5); x += wHeat + gap; }
    else if (heat === 2) { c.globalAlpha = blink ? 1 : 0.78; this.alarm('OVERHEAT', x + wHeat / 2, y, size, this.ap[K_OVER]); c.globalAlpha = 1; x += wHeat + gap; }
    if (fire) { c.globalAlpha = blink ? 1 : 0.8; this.alarm('ON FIRE', x + wFire / 2, y, size, this.ap[K_FIRE]); c.globalAlpha = 1; }
  }
  private alarmW(str: string, size: number) { return this.tw(str, size, 1, 0.16) + size * 2.8 + 6 * this.u; }

  // ------------------------------------------------------------------ WORK ORDER (top centre)
  private drawWorkOrder(s: HudState) {
    if (!this.woOpen) { this.drawWorkOrderFolded(s); return; }
    const u = this.u, c = this.c, x = this.w / 2, y0 = 30 * u;
    const pw = 400 * u, ph = 50 * u, k = 12 * u;
    // the plate: brow-shaped (chamfered both bottom corners)
    const g = this.g;
    g.beginPath(); g.moveTo(x - pw / 2, y0); g.lineTo(x + pw / 2, y0); g.lineTo(x + pw / 2 - k, y0 + ph); g.lineTo(x - pw / 2 + k, y0 + ph); g.closePath(); g.fill();
    c.beginPath(); c.moveTo(x - pw / 2, y0); c.lineTo(x + pw / 2, y0); c.lineTo(x + pw / 2 - k, y0 + ph); c.lineTo(x - pw / 2 + k, y0 + ph); c.closePath();
    c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    // cell dividers
    c.beginPath(); c.moveTo(x - 62 * u, y0 + 8 * u); c.lineTo(x - 62 * u, y0 + ph - 8 * u); c.moveTo(x + 62 * u, y0 + 8 * u); c.lineTo(x + 62 * u, y0 + ph - 8 * u);
    c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    // tape rails out to the sides with calibrated ticks (the heading-tape lineage)
    c.beginPath();
    for (let side = -1; side <= 1; side += 2) {
      const a = x + side * (pw / 2 + 6 * u), b = x + side * (pw / 2 + 170 * u);
      c.moveTo(a, y0 + 10 * u); c.lineTo(b, y0 + 10 * u);
      for (let i = 0; i <= 16; i++) { const tx = a + side * i * 10.5 * u; c.moveTo(tx, y0 + 10 * u); c.lineTo(tx, y0 + 10 * u + (i % 4 === 0 ? 6 : 3) * u); }
    }
    this.ks(this.hl, T.phos42);
    this.txt('WORK ORDER · WO-27B', x - pw / 2 - 176 * u, y0 + 30 * u, 7.5 * u, 0, T.phos42, 0, 0.22);
    this.txt('G.U.L.P. VISOR MK.II', x + pw / 2 + 176 * u, y0 + 30 * u, 7.5 * u, 1, T.phos42, 0, 0.22);
    // centre cell: wave
    const ws = s.waveState;
    this.txt('WAVE', x, y0 + 14 * u, 8 * u, 0.5, T.phos60, 0, 0.32, false);
    this.num(s.wave, x, y0 + 42 * u, 26 * u, 0.5, T.phos, 2, 0, 1, false);
    // left cell: tier
    const lx = x - 131 * u;
    this.txt('TIER', lx, y0 + 18 * u, 8 * u, 0.5, T.phos60, 0, 0.32, false);
    this.num(s.tier + 1, lx, y0 + 40 * u, 17 * u, 0.5, T.phos, 1, 0, 1, false);
    // right cell: robots / countdown
    const rx = x + 131 * u;
    if (ws === 1) {
      this.txt('ROBOTS', rx, y0 + 18 * u, 8 * u, 0.5, T.phos60, 0, 0.32, false);
      this.num(s.enemiesLeft, rx, y0 + 40 * u, 17 * u, 0.5, T.phos, 2, 0, 1, false);
    } else if (ws === 0 || ws === 2) {
      this.txt(ws === 0 ? 'NEXT WAVE' : 'NEXT TIER', rx, y0 + 18 * u, 8 * u, 0.5, T.amber, 0, 0.32, false);
      const tt = Math.max(0, Math.ceil(s.dirTimer));
      const cw = this.digitW(17 * u);
      const total = cw * 3 + 6 * u;
      let cx = rx - total / 2;
      cx += this.num(Math.floor(tt / 60), cx, y0 + 40 * u, 17 * u, 0, T.amber, 1, 0, 1, false);
      c.fillStyle = T.amber; c.fillRect(cx + 2 * u, y0 + 29 * u, 2 * u, 2 * u); c.fillRect(cx + 2 * u, y0 + 36 * u, 2 * u, 2 * u);
      this.num(tt % 60, cx + 6 * u, y0 + 40 * u, 17 * u, 0, T.amber, 2, 0, 1, false);
    } else {
      this.txt('ROBOTS', rx, y0 + 18 * u, 8 * u, 0.5, T.phos60, 0, 0.32, false);
      this.txt('--', rx, y0 + 40 * u, 17 * u, 0.5, T.phos42, 1, 0.1, false);
    }
    this.tokens(s, x, y0 + ph + 13 * u);
    this.drawObjective(s, y0 + ph + 40 * u);
    // state banners under the brow
    if (this.ap[K_CLEAR] > 0 && !s.objective) this.coachTab('ISLAND CLEARED', ' · WARP GATE CHARGING', x, y0 + ph + 44 * u, T.phos, this.ap[K_CLEAR]);
    if (this.ap[K_WIPE] > 0) {
      const blink = Math.floor(s.time * 4.4) % 5 < 3;
      c.globalAlpha = blink ? 1 : 0.8; this.alarm('POSSE WIPED', x, y0 + ph + 30 * u, 16 * u, this.ap[K_WIPE]); c.globalAlpha = 1;
    }
  }
  /** at rest the work order folds to one small tab: WAVE nn and the robots left */
  private drawWorkOrderFolded(s: HudState) {
    const u = this.u, c = this.c, x = this.w / 2, y0 = 30 * u;
    const pw = 132 * u, ph = 34 * u;
    this.seat(x - pw / 2, y0, pw, ph, 7 * u, 0.85, T.phos26);
    this.txt('WAVE', x - pw / 2 + 12 * u, y0 + 21.5 * u, 8 * u, 0, T.phos60, 0, 0.3, false);
    this.num(s.wave, x + 6 * u, y0 + 25 * u, 19 * u, 0.5, T.phos, 2, 0, 1, false);
    c.beginPath(); c.moveTo(x + 28 * u, y0 + 9 * u); c.lineTo(x + 28 * u, y0 + ph - 9 * u); c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    this.num(s.enemiesLeft, x + pw / 2 - 12 * u, y0 + 22 * u, 11 * u, 1, T.phos80, 1, 0, 1, false);
    this.tokens(s, x, y0 + ph + 12 * u);
    this.drawObjective(s, y0 + ph + 38 * u);
  }

  // ------------------------------------------------------------------ OBJECTIVE + BOSS (top centre)
  /** one line, always: what to do now. A small glass plate with a lit tick, the order in caps and
   *  the how in a quieter voice; a clock on the right when the order has one. */
  private drawObjective(s: HudState, y: number) {
    const ob = s.objective;
    const u = this.u, c = this.c, x = this.w / 2;
    if (ob && ob.title) {
      const col = ob.tone === 2 ? T.red : ob.tone === 1 ? T.amber : T.phos;
      const blink = ob.tone === 2 && Math.floor(s.time * 4.4) % 5 >= 3;
      const ts = 13 * u, ds = 11 * u;
      const w1 = this.tw(ob.title, ts, 1, 0.14), w2 = ob.detail ? this.tw(ob.detail, ds, 0, 0.12) + 14 * u : 0;
      const clock = ob.timer >= 0;
      const wc = clock ? this.digitW(12 * u) * 4 + 18 * u : 0;
      const tw = 22 * u + w1 + w2 + wc, h = 28 * u;
      // a smoked backing so the order reads over a bright boss or sky
      this.chamfer(c, x - tw / 2 - 10 * u, y - h / 2, tw + 20 * u, h, 6 * u, false); c.fillStyle = T.key; c.fill();
      this.seat(x - tw / 2 - 10 * u, y - h / 2, tw + 20 * u, h, 6 * u, 1, ob.tone ? (ob.tone === 2 ? T.red25 : T.amber25) : T.phos26);
      let cx = x - tw / 2;
      // the lit tick: a small diamond lamp in the order's colour
      c.beginPath(); this.diamond(cx + 6 * u, y, 4.2 * u); c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke();
      c.fillStyle = blink ? T.red60 : col; c.fill();
      cx += 18 * u;
      cx += this.txt(ob.title, cx, y + ts * 0.36, ts, 0, col, 1, 0.14, false);
      if (ob.detail) {
        c.beginPath(); c.moveTo(cx + 7 * u, y - 6 * u); c.lineTo(cx + 7 * u, y + 6 * u); this.ks(this.hl, T.phos26);
        cx += 14 * u;
        cx += this.txt(ob.detail, cx, y + ds * 0.36, ds, 0, T.phos, 0, 0.12, false);
      }
      if (clock) {
        const tt = Math.max(0, Math.ceil(ob.timer));
        let kx = cx + 14 * u;
        const tcol = ob.tone ? col : T.amber;
        kx += this.num(Math.floor(tt / 60), kx, y + 4.4 * u, 12 * u, 0, tcol, 1, 0, 1, false);
        c.fillStyle = tcol; c.fillRect(kx + 1.5 * u, y - 3 * u, 1.6 * u, 1.6 * u); c.fillRect(kx + 1.5 * u, y + 2 * u, 1.6 * u, 1.6 * u);
        this.num(tt % 60, kx + 5 * u, y + 4.4 * u, 12 * u, 0, tcol, 2, 0, 1, false);
      }
    }
    if (s.boss) this.drawBoss(s, y + 34 * u);
  }

  /** the boss bar: a long thin hull bar under a name plate, the boss's lamps on the left
   *  (lit = intact, gold ring = weak point open, dark = broken), the hyper-shield crack meter under it */
  private drawBoss(s: HudState, y: number) {
    const b = s.boss!;
    const u = this.u, c = this.c, x = this.w / 2;
    const bw = 460 * u, bh = 7 * u, x0 = x - bw / 2;
    // smoked backing plate under the whole block, so it holds over the boss's own bright hull
    this.chamfer(c, x0 - 14 * u, y - 16 * u, bw + 28 * u, (b.hyper || b.note ? 66 : 58) * u, 6 * u, false); c.fillStyle = T.key; c.fill();
    // name plate
    this.txt(b.name, x0, y, 12 * u, 0, T.phos, 1, 0.26, false);
    let nx = x0 + this.tw(b.name, 12 * u, 1, 0.26) + 10 * u;
    if (b.sub) nx += this.txt(b.sub, nx, y, 9.5 * u, 0, T.phos80, 0, 0.22, false) + 12 * u;
    if (b.count) this.txt(b.count, nx, y, 9.5 * u, 0, T.amber60, 1, 0.22, false);
    if (b.aimPart) this.txt(b.aimPart, x0 + bw, y, 10 * u, 1, b.aimPart.includes('CRIT') ? T.amber : T.phos60, 1, 0.22, false);
    const by = y + 7 * u;
    // the bar: glass seat, hull fill, quarter ticks
    this.seat(x0 - 3 * u, by - 3 * u, bw + 6 * u, bh + 6 * u, 3 * u, 1, T.phos26);
    const f = Math.max(0, Math.min(1, b.hp));
    c.fillStyle = b.hyper ? T.hyper : T.red; c.fillRect(x0, by, bw * f, bh);
    c.fillStyle = T.phos14; c.fillRect(x0 + bw * f, by, bw * (1 - f), bh);
    c.beginPath();
    for (const q of [1 / 3, 2 / 3]) { c.moveTo(x0 + bw * q, by - 3 * u); c.lineTo(x0 + bw * q, by + bh + 3 * u); }
    this.ks(this.hl * 1.3, T.phos60);
    // the boss's lamps, left to right (Cerberus: FURNACE, DYNAMO, MAGNET)
    for (let i = 0; i < b.pips.length && i < 4; i++) {
      const pp = b.pips[i];
      const hx = x0 + 14 * u + i * 52 * u, hy = by + bh + 17 * u;
      const broken = pp.broken, open = pp.open;
      this.disc(hx, hy, 8.5 * u, 0.85, false);
      if (!broken) {
        this.arc(hx, hy, 6 * u, -Math.PI / 2, -Math.PI / 2 + TAU * Math.max(0.02, pp.frac), 2.4 * u, T.phos);
        if (open) this.arc(hx, hy, 9.5 * u, 0, TAU, 1.8 * u, T.amber);
      } else {
        c.beginPath(); c.moveTo(hx - 4 * u, hy - 4 * u); c.lineTo(hx + 4 * u, hy + 4 * u); c.moveTo(hx + 4 * u, hy - 4 * u); c.lineTo(hx - 4 * u, hy + 4 * u);
        this.ks(1.8 * u, T.phos42);
      }
      this.txt(pp.label, hx + 13 * u, hy + 3.5 * u, 9 * u, 0, broken ? T.phos42 : T.phos80, 0, 0.2, false);
    }
    // the Ruff: while it's up, a magenta crack meter fills toward SHATTER
    if (b.hyper) {
      const rx = x0 + bw - 190 * u, ry = by + bh + 15 * u;
      this.txt(b.meter || 'HYPER SHIELD', rx, ry + 3 * u, 7.5 * u, 0, T.hyper, 1, 0.22, false);
      const mx = rx + 76 * u, mw = 114 * u;
      c.fillStyle = T.phos14; c.fillRect(mx, ry - 1.5 * u, mw, 3 * u);
      c.fillStyle = T.hyper; c.fillRect(mx, ry - 1.5 * u, mw * Math.min(1, b.crack), 3 * u);
    } else if (b.note) {
      const blink = Math.floor(s.time * 4.4) % 5 < 3;
      this.txt(b.note, x0 + bw, by + bh + 20 * u, 8.5 * u, 1, blink ? T.amber : T.amber60, 1, 0.22, false);
    }
    if (b.warn) {
      const blink = Math.floor(s.time * 4.4) % 5 < 3;
      c.globalAlpha = blink ? 1 : 0.8; this.alarm(b.warn, x, by + bh + 52 * u, 15 * u, 1); c.globalAlpha = 1;
    }
  }
  /** posse tokens (lives): 0.45 at rest, full for 1.6 s on change */
  private tokens(s: HudState, x: number, ty: number) {
    const u = this.u, c = this.c;
    c.globalAlpha = this.tokBright ? 1 : 0.45;
    const n = Math.max(5, s.lives), sp = 13 * u;
    const tx0 = x - ((n - 1) * sp) / 2;
    c.beginPath();
    for (let i = s.lives; i < n; i++) this.diamond(tx0 + i * sp, ty, 4 * u);
    this.ks(this.hl, T.phos42);
    if (s.lives > 0) {
      c.beginPath();
      for (let i = 0; i < s.lives; i++) this.diamond(tx0 + i * sp, ty, 4.4 * u);
      c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke(); c.fillStyle = s.lives <= 1 ? T.red : T.phos; c.fill();
    }
    this.txt('TOKENS', tx0 - 12 * u, ty + 3 * u, 7.5 * u, 1, T.phos42, 0, 0.25);
    c.globalAlpha = 1;
  }

  // ------------------------------------------------------------------ POSSE tabs (top-left)
  private drawPosse(s: HudState) {
    const u = this.u;
    const x = 40 * u;
    let y = 36 * u;
    y += 10 * u;
    for (let i = 0; i < s.posse.length && i < 3; i++) {
      if (this.tmOpen[i]) { this.posseTab(s.posse[i], x, y); y += 50 * u; } else { this.posseSlim(s.posse[i], x, y); y += 30 * u; }
    }
  }
  /** a healthy, quiet teammate: one slim row (spine, name, hull hairline) */
  private posseSlim(t: HudTeammate, x: number, y: number) {
    const u = this.u, c = this.c, w = 176 * u, h = 24 * u;
    this.seat(x, y, w, h, 6 * u, 0.7, T.phos14);
    c.fillStyle = SLOT[t.slot & 3]; c.fillRect(x + 4 * u, y + 6 * u, 3 * u, h - 12 * u);
    this.txt(t.name.length > 10 ? t.name.slice(0, 10) : t.name, x + 13 * u, y + 15.5 * u, 10 * u, 0, T.phos80, 1, 0.08, false);
    const bx = x + w - 66 * u, bw = 56 * u, by = y + h / 2, hp = t.hp / Math.max(1, t.hpMax);
    c.beginPath(); c.moveTo(bx, by); c.lineTo(bx + bw, by); c.lineWidth = 3 * u; c.strokeStyle = T.phos14; c.stroke();
    c.beginPath(); c.moveTo(bx, by); c.lineTo(bx + bw * hp, by); c.strokeStyle = T.phos60; c.stroke();
  }
  private posseTab(t: HudTeammate, x: number, y: number) {
    const u = this.u, c = this.c, w = 216 * u, h = 42 * u, k = 8 * u;
    const down = t.phase === 1, dead = t.phase === 2, lost = !t.connected;
    c.globalAlpha = lost ? 0.5 : t.paused ? 0.65 : 1;
    this.seat(x, y, w, h, k, 1, down ? T.red60 : T.phos26);
    // slot spine
    c.fillStyle = SLOT[t.slot & 3]; c.fillRect(x + 4 * u, y + k, 3 * u, h - 2 * k);
    this.txt(t.name.length > 12 ? t.name.slice(0, 12) : t.name, x + 14 * u, y + 15 * u, 11.5 * u, 0, T.phos, 1, 0.08, false);
    this.txt(CLS3[t.cls] ?? '', x + w - 10 * u, y + 14 * u, 7.5 * u, 1, T.phos42, 0, 0.2, false);
    if (down || dead) {
      const pw = 62 * u, px = x + w - pw - 10 * u, py = y + 21 * u, blink = Math.floor(this.s.time * 2.2) % 2 === 0;
      this.chamfer(c, px, py, pw, 14 * u, 4 * u); c.fillStyle = dead ? 'rgba(120,20,16,0.9)' : T.plateRed; c.fill();
      this.txt(dead ? 'GONE' : 'DOWN', px + pw / 2, py + 10.3 * u, 8.5 * u, 0.5, blink || dead ? T.plateText : 'rgba(255,246,236,0.6)', 1, 0.24, false);
      if (down) {
        // eight-lamp revive bar
        const bx = x + 14 * u, lw = 9 * u, sp = 11.5 * u, by = y + 25 * u;
        const lit = Math.round(t.reviveFrac * 8);
        for (let i = 0; i < 8; i++) { c.fillStyle = i < lit ? T.phos : T.red25; c.fillRect(bx + i * sp, by, lw, 6 * u); }
        this.num(Math.round(t.dist), x + 14 * u + 8 * sp + 4 * u, y + 32 * u, 8.5 * u, 0, T.phos60, 1, 0, 1, false);
      }
    } else if (lost || t.paused) {
      this.txt(lost ? 'LINK LOST' : 'SKY HOLD', x + 14 * u, y + 33 * u, 8.5 * u, 0, T.phos60, 1, 0.24, false);
    } else {
      const bx = x + 14 * u, bw = w - 26 * u;
      const hp = t.hp / Math.max(1, t.hpMax), sh = t.shieldMax > 0 ? t.shield / t.shieldMax : 0, ht = t.heat / Math.max(1, t.heatMax);
      // shield hairline, hull in 10 segments, heat hairline
      c.beginPath(); c.moveTo(bx, y + 22.5 * u); c.lineTo(bx + bw, y + 22.5 * u); c.lineWidth = 2 * u; c.strokeStyle = T.shield20; c.stroke();
      if (sh > 0) { c.beginPath(); c.moveTo(bx, y + 22.5 * u); c.lineTo(bx + bw * sh, y + 22.5 * u); c.strokeStyle = T.shield; c.stroke(); }
      const sy = y + 29 * u;
      c.beginPath(); for (let i = 0; i < 10; i++) { c.moveTo(bx + (bw * i) / 10, sy); c.lineTo(bx + (bw * (i + 1)) / 10 - 2 * u, sy); }
      c.lineWidth = 5 * u; c.strokeStyle = T.phos14; c.stroke();
      if (hp > 0) {
        const hn = hp * 10;
        c.beginPath(); for (let i = 0; i < 10 && i < hn; i++) { c.moveTo(bx + (bw * i) / 10, sy); c.lineTo(bx + Math.min((bw * (i + 1)) / 10 - 2 * u, (bw * hn) / 10), sy); }
        c.strokeStyle = hp < 0.3 ? T.red : T.phos; c.stroke();
      }
      if (ht > 0.02) { c.beginPath(); c.moveTo(bx, y + 35 * u); c.lineTo(bx + bw * Math.min(1, ht), y + 35 * u); c.lineWidth = 1.5 * u; c.strokeStyle = ht >= 0.8 ? T.red : T.amber60; c.stroke(); }
    }
    c.globalAlpha = 1;
  }

  // ------------------------------------------------------------------ name tags + edge arrows
  private drawTags(s: HudState) {
    const u = this.u, c = this.c;
    for (let i = 0; i < s.posse.length; i++) {
      const t = s.posse[i];
      if (!t.connected) continue;
      const col = SLOT[t.slot & 3];
      if (t.onScreen) {
        if (t.phase === 1) continue;
        const x = t.sx, y = t.sy - 12 * u;
        c.globalAlpha = t.paused ? 0.5 : 1;
        c.beginPath(); c.moveTo(x - 5 * u, y - 7 * u); c.lineTo(x + 5 * u, y - 7 * u); c.lineTo(x, y); c.closePath();
        c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke(); c.fillStyle = col; c.fill();
        const nm = t.name.length > 12 ? t.name.slice(0, 12) : t.name;
        this.tagTab(nm, -1, x, y - 25 * u, T.phos, col);
        const bw = 34 * u, hp = t.hp / Math.max(1, t.hpMax);
        c.beginPath(); c.moveTo(x - bw / 2, y - 12 * u); c.lineTo(x + bw / 2, y - 12 * u); this.ks(2 * u, T.phos14);
        c.beginPath(); c.moveTo(x - bw / 2, y - 12 * u); c.lineTo(x - bw / 2 + bw * hp, y - 12 * u); c.lineWidth = 2 * u; c.strokeStyle = hp < 0.3 ? T.red : T.phos; c.stroke();
        c.globalAlpha = 1;
      } else {
        if (t.phase === 1 || this.tmOpen[i] || t.dist > 45) this.edgeArrow(t.angle, col, t.dist, t.name.length > 3 ? t.name.slice(0, 3) : t.name, t.phase === 1);
      }
    }
  }
  private edgePos(a: number) {
    const u = this.u;
    const rx = this.w / 2 - 90 * u, ry = this.h / 2 - 86 * u;
    let x = this.w / 2 + Math.cos(a) * rx, y = this.h / 2 + Math.sin(a) * ry;
    // keep clear of the two ring instruments: slide out of their discs radially
    for (let i = 0; i < 2; i++) {
      const cx = i ? this.ox : this.vx, cy = this.vy, dx = x - cx, dy = y - cy, d = Math.hypot(dx, dy), R = 175 * u;
      if (d < R) { const k = R / Math.max(1, d); x = cx + dx * k; y = cy + dy * k; }
    }
    this.ep[0] = x; this.ep[1] = y;
    return this.ep;
  }
  private edgeArrow(a: number, col: string, dist: number, label: string, alarm: boolean) {
    const u = this.u, c = this.c;
    const p = this.edgePos(a), x = p[0], y = p[1];
    if (!this.vis(x - 110 * u, y - 60 * u, 220 * u, 120 * u)) return;
    const ca = Math.cos(a), sa = Math.sin(a), r = 11 * u;
    c.beginPath();
    c.moveTo(x + ca * r, y + sa * r);
    c.lineTo(x - sa * r * 0.8 - ca * r * 0.2, y + ca * r * 0.8 - sa * r * 0.2);
    c.lineTo(x + ca * r * 0.2, y + sa * r * 0.2);
    c.lineTo(x + sa * r * 0.8 - ca * r * 0.2, y - ca * r * 0.8 - sa * r * 0.2);
    c.closePath();
    c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke(); c.fillStyle = alarm ? T.red : col; c.fill();
    const lx = x - ca * 44 * u, ly = y - sa * 30 * u;
    this.tagTab(label, dist, lx, ly, alarm ? T.red : T.phos, alarm ? T.red : col);
  }
  private distLabel(dist: number, x: number, y: number) { this.tagTab('', dist, x, y, T.phos, T.phos42); }
  /** a world label on a small chamfered glass tab (the notice treatment): NAME  34M, centred on x, y */
  private tagTab(label: string, dist: number, x: number, y: number, col: string, spine: string) {
    const u = this.u, c = this.c, size = 13 * u, ds = 12 * u;
    const lw = label ? this.tw(label, size, 1, 0.08) : 0;
    const d = Math.round(dist);
    const nw = dist >= 0 ? (d >= 100 ? 3 : d >= 10 ? 2 : 1) * this.digitW(ds) + this.tw('M', ds * 0.8, 0, 0) + 2 * u : 0;
    const gap = lw > 0 && nw > 0 ? 7 * u : 0;
    const w = lw + gap + nw + 16 * u, h = 19 * u, x0 = Math.round(x - w / 2), y0 = Math.round(y - h / 2);
    this.chamfer(c, x0, y0, w, h, 4 * u); c.fillStyle = T.tabFill; c.fill();
    this.seat(x0, y0, w, h, 4 * u, 1, T.phos26);
    c.fillStyle = spine; c.fillRect(x0 + 3 * u, y0 + 5 * u, 1.6 * u, h - 10 * u);
    let cx = x0 + 9 * u;
    const by = y0 + h / 2 + size * 0.36;
    if (lw > 0) cx += this.txt(label, cx, by, size, 0, col, 1, 0.08, false) + gap;
    if (nw > 0) {
      const w2 = this.num(d, cx, by, ds, 0, T.phos80, 1, 0, 1, false);
      this.txt('M', cx + w2 + 2 * u, by, ds * 0.8, 0, T.phos60, 0, 0, false);
    }
  }

  // ------------------------------------------------------------------ world markers
  private labX = new Float32Array(32);
  private labY = new Float32Array(32);
  private nLab = 0;
  private drawMarkers(s: HudState) {
    const u = this.u, c = this.c;
    this.nLab = 0;
    for (let i = 0; i < s.markers.length; i++) {
      const m = s.markers[i];
      const col = m.kind === 'gate' || m.kind === 'train' ? T.phos : m.kind === 'bell' || m.kind === 'powerup' || m.kind === 'crate' ? T.amber : m.kind === 'primed' ? T.prime : m.kind === 'downed' ? T.amber : m.kind === 'ping' ? ((m.slot ?? 0) < 0 ? T.red : SLOT[(m.slot ?? 0) % SLOT.length]) : T.red;
      if (m.kind === 'ping') { this.drawPing(m, col); continue; }
      if (!m.onScreen) {
        if (m.kind === 'primed') continue;
        const way = m.kind === 'gate' || m.kind === 'bell' || m.kind === 'train' || m.kind === 'bonus' || m.kind === 'powerup' || m.kind === 'crate';
        this.edgeArrow(m.angle, col, m.dist, m.kind === 'gate' ? 'GATE' : m.kind === 'bonus' ? 'BONUS' : m.kind === 'powerup' ? 'PWR' : m.kind === 'crate' ? 'CRATE' : m.kind === 'bell' ? 'BELL' : m.kind === 'train' ? 'TRAIN' : m.kind === 'downed' ? 'DOWN' : m.kind === 'bomb' ? 'BOMB' : 'INBD', !way);
        continue;
      }
      const x = m.sx, y = m.sy;
      if (!this.vis(x - 80 * u, y - 70 * u, 160 * u, 150 * u)) continue;
      if (m.kind === 'spawn') {
        // inbound: four brackets closing on the drop point + countdown
        const r = (15 + Math.min(1, m.t / 3) * 12) * u;
        c.beginPath();
        for (let q = 0; q < 4; q++) {
          const a = q * Math.PI / 2 + Math.PI / 4, ca = Math.cos(a), sa = Math.sin(a);
          c.moveTo(x + ca * r - sa * 6 * u, y + sa * r + ca * 6 * u); c.lineTo(x + ca * r, y + sa * r); c.lineTo(x + ca * r + sa * 6 * u, y + sa * r - ca * 6 * u);
        }
        this.ks(2 * u, T.red);
        // a pack dropping together gets one plate + countdown, not a pile of overlapping ones
        let crowded = false;
        for (let k = 0; k < this.nLab; k++) if (Math.abs(this.labX[k] - x) < 70 * u && Math.abs(this.labY[k] - y) < 48 * u) { crowded = true; break; }
        if (crowded) continue;
        if (this.nLab < this.labX.length) { this.labX[this.nLab] = x; this.labY[this.nLab] = y; this.nLab++; }
        const tw = this.tw('INBOUND', 8 * u, 1, 0.22) + 12 * u;
        this.chamfer(c, x - tw / 2, y - r - 22 * u, tw, 13 * u, 3 * u); c.fillStyle = T.plateRed; c.fill();
        this.txt('INBOUND', x, y - r - 12.5 * u, 8 * u, 0.5, T.plateText, 1, 0.22, false);
        if (m.t > 0) this.num(m.t, x, y + 4 * u, 11 * u, 0.5, T.phos, 1, 1);
      } else if (m.kind === 'bomb') {
        const r = 18 * u;
        c.setLineDash([5 * u, 3.5 * u]); this.arc(x, y, r, 0, TAU, 2 * u, T.red); c.setLineDash([]);
        c.beginPath(); c.moveTo(x - r - 7 * u, y); c.lineTo(x - r + 5 * u, y); c.moveTo(x + r - 5 * u, y); c.lineTo(x + r + 7 * u, y);
        c.moveTo(x, y - r - 7 * u); c.lineTo(x, y - r + 5 * u); c.moveTo(x, y + r - 5 * u); c.lineTo(x, y + r + 7 * u);
        this.ks(2 * u, T.red);
        if (m.t > 0) this.num(m.t, x, y + 4 * u, 11 * u, 0.5, T.phos, 1, 1);
      } else if (m.kind === 'downed') {
        const r = 20 * u, blink = Math.floor(s.time * 1.8) % 2 === 0;
        this.disc(x, y, r + 8 * u, 0.8, false);
        this.arc(x, y, r, 0, TAU, 4 * u, T.amber25, true);
        if (m.t > 0) { c.beginPath(); c.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + TAU * m.t); this.ks(4 * u, T.phos, false); }
        c.beginPath(); c.moveTo(x - 7 * u, y); c.lineTo(x + 7 * u, y); c.moveTo(x, y - 7 * u); c.lineTo(x, y + 7 * u);
        this.ks(3.5 * u, blink ? T.amber : T.amber60);
        if (m.dist < 4) {
          const g = this.bind(BIND.revive);
          const tw = this.tw('HOLD', 9.5 * u, 1, 0.2) + this.tw('REVIVE', 9.5 * u, 1, 0.2) + 36 * u;
          const gx = x - tw / 2, gy = y + r + 20 * u;
          this.seat(gx - 6 * u, gy - 11 * u, tw + 12 * u, 22 * u, 5 * u, 1, T.phos26);
          let cx = gx;
          cx += this.txt('HOLD', cx, gy + 3.4 * u, 9.5 * u, 0, T.phos, 1, 0.2, false) + 6 * u;
          cx += this.glyph(g, cx, gy, 13 * u) + 6 * u;
          this.txt('REVIVE', cx, gy + 3.4 * u, 9.5 * u, 0, T.phos, 1, 0.2, false);
        } else this.distLabel(m.dist, x, y + r + 18 * u);
      } else if (m.kind === 'primed') {
        const r = 7 * u;
        c.beginPath(); this.diamond(x, y, r);
        c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke(); c.fillStyle = T.prime; c.fill();
      } else {
        // gate / bell / train: tall diamond waypoint with its charge
        const r = 12 * u;
        c.beginPath(); c.moveTo(x, y - r * 1.5); c.lineTo(x + r, y); c.lineTo(x, y + r * 1.5); c.lineTo(x - r, y); c.closePath();
        this.ks(1.6 * u, col);
        if (m.t > 0) { c.beginPath(); c.moveTo(x, y + r * 1.5); c.lineTo(x, y + r * 1.5 - r * 3 * Math.min(1, m.t)); this.ks(3 * u, col, false); }
        const lab = m.label || (m.kind === 'bell' ? 'RING THE BELL' : m.kind === 'train' ? 'BOARD' : m.kind === 'bonus' ? 'BONUS ROUND' : m.kind === 'powerup' ? 'POWER-UP' : m.kind === 'crate' ? 'ORDNANCE' : 'GATE');
        this.tagTab(lab, m.dist, x, y - r * 1.5 - 16 * u, col, col);
      }
    }
  }

  /** a posse ping: a slot-coloured pin that drops in, then the tag; off screen, an edge arrow */
  private drawPing(m: HudMarker, col: string) {
    const u = this.u, c = this.c;
    if (!m.onScreen) { this.edgeArrow(m.angle, col, m.dist, m.label ?? 'PING', false); return; }
    const x = m.sx, y = m.sy;
    if (!this.vis(x - 80 * u, y - 70 * u, 160 * u, 150 * u)) return;
    const drop = Math.max(0, 1 - m.t / 0.25), fade = m.t > 5 ? Math.max(0, 1 - (m.t - 5)) : 1;
    c.globalAlpha = fade;
    const py = y - drop * 18 * u, r = 9 * u;
    // the pin: diamond head on a stem
    c.beginPath(); c.moveTo(x, py + r * 2.2); c.lineTo(x, py + r); this.ks(2 * u, col);
    c.beginPath(); this.diamond(x, py, r);
    c.lineWidth = this.kp * 2; c.strokeStyle = T.key; c.stroke(); c.fillStyle = col; c.fill();
    // a ring that spreads once on arrival
    if (m.t < 0.6) { const k = m.t / 0.6; c.globalAlpha = fade * (1 - k); this.arc(x, py, r + k * 26 * u, 0, TAU, 2 * u, col); c.globalAlpha = fade; }
    this.tagTab(m.label ?? '', m.dist, x, py - r - 16 * u, T.phos, col);
    c.globalAlpha = 1;
  }

  // ------------------------------------------------------------------ notices (top-right)
  private drawNotices(s: HudState) {
    const u = this.u, c = this.c;
    const R = this.w - 40 * u;
    let y = s.showNet ? 300 * u : 40 * u;
    if (s.showNet) return; // the diag panel owns the corner
    let shown = 0;
    for (let i = 0; i < s.notices.length && shown < 4; i++) {
      const n = s.notices[i];
      const a = this.noticeAlpha(n.age);
      if (a <= 0) continue;
      const fresh = n.age < 1;
      c.globalAlpha = a; this.g.globalAlpha = a;
      const size = 11 * u;
      const w = this.tw(n.text, size, 1, 0.08) + 28 * u, h = 22 * u;
      this.seat(R - w, y, w, h, 5 * u, a, T.phos26);
      // right spine: amber while fresh
      c.fillStyle = fresh ? T.amber : T.phos42; c.fillRect(R - 5 * u, y + 5 * u, 2 * u, h - 10 * u);
      this.txt(n.text, R - 12 * u, y + h / 2 + size * 0.36, size, 1, fresh ? T.phos : T.phos80, 1, 0.08, false);
      y += 27 * u; shown++;
    }
    c.globalAlpha = 1; this.g.globalAlpha = 1;
  }

  // ------------------------------------------------------------------ downed / dead
  private drawDowned(s: HudState) {
    const u = this.u, c = this.c, x = this.w / 2, y = this.h / 2 - 20 * u;
    const p = this.ap[K_DOWN], e = this.ease(Math.min(1, p / 0.8));
    const blink = Math.floor(s.time * 4.4) % 5 < 3;
    if (s.phase === 2) {
      const cy = this.h / 2 + 40 * u;
      this.alarm(s.lives > 0 ? 'LIABILITY EVENT' : 'CONTRACT TERMINATED', x, cy, 22 * u, p);
      if (e > 0.9) {
        if (s.lives > 0) {
          const str = 'REISSUE PENDING · TOKENS ';
          const tw = this.tw(str, 11 * u, 1, 0.16) + this.digitW(14 * u) * (s.lives >= 10 ? 2 : 1);
          this.seat(x - tw / 2 - 16 * u, cy + 58 * u, tw + 32 * u, 24 * u, 5 * u, 1, T.phos26);
          this.txt(str, x - tw / 2, cy + 74 * u, 11 * u, 0, T.phos80, 1, 0.16, false);
          this.num(s.lives, x + tw / 2, cy + 75 * u, 14 * u, 1, T.phos, 1, 0, 1, false);
        } else this.coachTab('OBSERVER STATUS', ' GRANTED', x, cy + 70 * u, T.phos, p);
      }
      return;
    }
    // CLAIM WINDOW dial: amber, last 10 s red 2.2 Hz
    const secs = Math.max(0, s.bleedOut), frac = Math.min(1, secs / 30);
    const late = secs <= 10;
    const R = (96 + (1 - e) * 30) * u;
    c.globalAlpha = Math.min(1, p * 2);
    this.disc(x, y, R + 22 * u, 1);
    c.beginPath();
    for (let i = 0; i < 60; i++) {
      const a = -Math.PI / 2 + (TAU * i) / 60, l = i % 5 === 0 ? 8 * u : 4 * u;
      c.moveTo(x + Math.cos(a) * (R + 9 * u), y + Math.sin(a) * (R + 9 * u)); c.lineTo(x + Math.cos(a) * (R + 9 * u + l), y + Math.sin(a) * (R + 9 * u + l));
    }
    this.ks(this.hl, T.phos42);
    const col = late ? (blink ? T.red : T.red60) : T.amber;
    this.arc(x, y, R, 0, TAU, 8 * u, late ? T.red25 : T.amber25);
    if (frac > 0) { c.beginPath(); c.arc(x, y, R, -Math.PI / 2, -Math.PI / 2 + TAU * frac); this.ks(8 * u, col, false); }
    // revive progress (inner)
    this.arc(x, y, R - 15 * u, 0, TAU, 3 * u, T.phos14);
    if (s.reviveFrac > 0) { c.beginPath(); c.arc(x, y, R - 15 * u, -Math.PI / 2, -Math.PI / 2 + TAU * s.reviveFrac); this.ks(4 * u, T.phos, false); }
    this.txt('CLAIM WINDOW', x, y - 30 * u, 9 * u, 0.5, T.phos60, 0, 0.3);
    this.num(Math.ceil(secs), x, y + 20 * u, 46 * u, 0.5, late ? col : T.amber, 2);
    this.txt('SEC', x, y + 40 * u, 8 * u, 0.5, T.phos42, 0, 0.3);
    // state card under the dial
    const cy = y + R + 34 * u;
    c.globalAlpha = blink || !late ? 1 : 0.85;
    this.alarm('CONTRACTOR DOWN', x, cy, 18 * u, p);
    c.globalAlpha = 1;
    if (e > 0.9) {
      const hy = cy + 66 * u;
      if (s.reviveFrac > 0) {
        // eight-lamp revive bar
        const lw = 14 * u, sp = 17 * u, bx = x - (8 * sp) / 2;
        this.seat(bx - 14 * u, hy - 14 * u, 8 * sp + 24 * u, 28 * u, 5 * u, 1, T.phos26);
        const lit = Math.round(s.reviveFrac * 8);
        for (let i = 0; i < 8; i++) { c.fillStyle = i < lit ? T.phos : T.phos14; c.fillRect(bx + i * sp, hy - 4 * u, lw, 8 * u); }
      } else {
        const g = this.bind(BIND.revive);
        const size = 11 * u;
        const w1 = this.tw('POSSE:', size, 0, 0.16), w2 = this.tw('HOLD', size, 1, 0.16), w3 = this.tw('TO REVIVE YOU', size, 1, 0.16);
        const gw = 18 * u, total = w1 + w2 + w3 + gw + 26 * u;
        this.seat(x - total / 2 - 14 * u, hy - 14 * u, total + 28 * u, 28 * u, 5 * u, 1, T.phos26);
        let cx = x - total / 2;
        cx += this.txt('POSSE:', cx, hy + size * 0.36, size, 0, T.phos60, 0, 0.16, false) + 8 * u;
        cx += this.txt('HOLD', cx, hy + size * 0.36, size, 0, T.phos, 1, 0.16, false) + 6 * u;
        cx += this.glyph(g, cx, hy, 15 * u) + 6 * u;
        this.txt('TO REVIVE YOU', cx, hy + size * 0.36, size, 0, T.phos, 1, 0.16, false);
      }
    }
  }

  // ------------------------------------------------------------------ sky hold / pause
  private drawSky(s: HudState) {
    const u = this.u, c = this.c, x = this.w / 2, y = this.h / 2 - 170 * u;
    const p = this.ap[K_SKY], e = this.ease(Math.min(1, p / 0.8));
    const title = s.frozen ? 'POSSE PAUSED' : 'SKY HOLD';
    const size = 20 * u;
    const tw = this.tw(title, size, 1, 0.24);
    const ph = 46 * u, pw = (tw + 80 * u) * e;
    this.seat(x - pw / 2, y - ph / 2, pw, ph, 12 * u, 1, T.phos60, true);
    if (e < 0.85) return;
    // registration corners outside the plate
    c.beginPath();
    const ox = x - pw / 2 - 8 * u, oy = y - ph / 2 - 8 * u, ow = pw + 16 * u, oh = ph + 16 * u, k = 10 * u;
    c.moveTo(ox, oy + k); c.lineTo(ox, oy); c.lineTo(ox + k, oy); c.moveTo(ox + ow - k, oy); c.lineTo(ox + ow, oy); c.lineTo(ox + ow, oy + k);
    c.moveTo(ox + ow, oy + oh - k); c.lineTo(ox + ow, oy + oh); c.lineTo(ox + ow - k, oy + oh); c.moveTo(ox + k, oy + oh); c.lineTo(ox, oy + oh); c.lineTo(ox, oy + oh - k);
    this.ks(this.hl * 1.4, T.phos60);
    this.txt(title, x, y + size * 0.36, size, 0.5, T.phos, 1, 0.24, false);
    const sub = s.frozen ? 'THE WORLD WAITS · G.U.L.P. BILLS BY THE HOUR' : 'LIFTED CLEAR · NO CHARGE FOR THE VIEW';
    this.txt(sub, x, y + 44 * u, 9 * u, 0.5, T.phos80, 0, 0.2);
    if (!s.frozen) {
      const bw = 220 * u, by = y + 58 * u;
      c.beginPath(); c.moveTo(x - bw / 2, by); c.lineTo(x + bw / 2, by); this.ks(3 * u, T.phos14);
      c.beginPath(); c.moveTo(x - bw / 2, by); c.lineTo(x - bw / 2 + bw * s.skyT, by); this.ks(3 * u, T.phos, false);
    }
  }

  // ------------------------------------------------------------------ net / frame debug (F3)
  private drawNet(s: HudState) {
    const u = this.u, c = this.c, n = s.net;
    const w = 262 * u, h = 250 * u, x = this.w - w - 32 * u, y = 32 * u;
    this.seat(x, y, w, h, 12 * u, 1, T.phos42);
    this.txt('DIAG · NET-FRM', x + 14 * u, y + 20 * u, 9.5 * u, 0, T.phos, 1, 0.2, false);
    this.txt('TECH FORM 9', x + w - 14 * u, y + 19 * u, 7.5 * u, 1, T.phos42, 0, 0.2, false);
    c.beginPath(); c.moveTo(x + 14 * u, y + 28 * u); c.lineTo(x + w - 14 * u, y + 28 * u); c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    const rowH = 16 * u, sz = 9 * u;
    let ry = y + 46 * u;
    const L = x + 14 * u, M = x + w / 2 + 6 * u;
    const warn = (bad: boolean) => (bad ? T.amber : T.phos);
    const row = (lab: string, v: number, dec: number, unit: string, col: string, xx: number) => {
      this.txt(lab, xx, ry, 7.5 * u, 0, T.phos60, 0, 0.16, false);
      this.num(v, xx + 92 * u, ry, sz, 1, col, 1, dec, 1, false);
      this.txt(unit, xx + 95 * u, ry, 6.5 * u, 0, T.phos42, 0, 0.1, false);
    };
    row('PING', n.ping, 0, 'MS', warn(n.ping > 120), L); row('FPS', n.fps, 0, '', warn(n.fps < 55), M); ry += rowH;
    row('JITTER', n.jitter, 1, 'MS', warn(n.jitter > 15), L); row('FRAME', n.frameMs, 2, 'MS', warn(n.frameMs > 16.7), M); ry += rowH;
    row('LOSS', n.loss * 100, 1, '%', warn(n.loss > 0.02), L); row('DRAWS', n.draws, 0, '', warn(n.draws > 400), M); ry += rowH;
    row('CUSHION', n.cushion, 1, 'TK', warn(n.cushion < 0.5), L); row('TRIS', n.tris / 1000, 1, 'K', T.phos, M); ry += rowH;
    row('CORR', n.corrections, 0, '/S', warn(n.corrections > 2), L); row('TICK', n.tickRate, 1, 'HZ', T.phos, M); ry += rowH;
    row('INTERP', n.interp, 0, 'MS', T.phos, L); row('KB IN', n.kbpsIn, 1, 'KBPS', T.phos, M); ry += rowH;
    row('KB OUT', n.kbpsOut, 1, 'KBPS', T.phos, M); ry += rowH;
    const gx = x + 14 * u, gw = w - 28 * u, gh = 48 * u, gy = ry;
    c.beginPath(); c.moveTo(gx, gy + gh); c.lineTo(gx + gw, gy + gh);
    for (let i = 1; i <= 3; i++) { c.moveTo(gx, gy + gh - (gh * i) / 3); c.lineTo(gx + 4 * u, gy + gh - (gh * i) / 3); }
    c.lineWidth = this.hl; c.strokeStyle = T.phos26; c.stroke();
    this.spark(this.hPing, 200, gx, gy, gw, gh, T.phos);
    this.spark(this.hFrame, 33.3, gx, gy, gw, gh, T.amber);
    this.txt('PING 0-200', gx + 8 * u, gy + 8 * u, 6.5 * u, 0, T.phos60, 0, 0.1, false);
    this.txt('FRM 0-33', gx + gw, gy + 8 * u, 6.5 * u, 1, T.amber, 0, 0.1, false);
  }
  private spark(buf: Float32Array, max: number, x: number, y: number, w: number, h: number, col: string) {
    const c = this.c;
    c.beginPath();
    for (let i = 0; i < NET_HIST; i++) {
      const v = buf[(this.hIdx + i) % NET_HIST];
      const px = x + (w * i) / (NET_HIST - 1), py = y + h - Math.min(1, v / max) * h;
      if (i === 0) c.moveTo(px, py); else c.lineTo(px, py);
    }
    c.lineWidth = 1.2 * this.u; c.strokeStyle = col; c.stroke();
  }
}

export type { HudMarker };
